/* build-poke: 1746574800 */
import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Rocket, Bot, FlaskConical, CheckSquare, Square, ClipboardCopy, ClipboardList, CheckCircle, Loader2, FileText, X, Package, ChevronDown, Trash2, ArrowUpDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import FeedbackStatsBar from '@/components/feedback/FeedbackStatsBar';
import FeedbackFilters from '@/components/feedback/FeedbackFilters';
import FeedbackCard from '@/components/feedback/FeedbackCard';
import FeedbackBotChat from '@/components/feedback/FeedbackBotChat';
import TesterCasterPanel from '@/components/feedback/TesterCasterPanel';
import BriefViewer from '@/pages/BriefViewer';
/* Feedback.jsx v3.1 — restyled 2026-04-06 */

// ── Lifecycle log helper (shared across Feedback page) ──
function appendLifecycleEvent(existingLog, event, actor, metadata, cycle) {
  var log = [];
  try {
    log = typeof existingLog === 'string' ? JSON.parse(existingLog) : (Array.isArray(existingLog) ? existingLog : []);
  } catch (e) { log = []; }
  log.push({
    event: event,
    timestamp: new Date().toISOString(),
    actor: actor || 'system',
    metadata: metadata || {},
    cycle: cycle || 0
  });
  return JSON.stringify(log);
}

export default function Feedback() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pageMode, setPageMode] = useState('feedback'); // 'feedback' | 'testercaster'
  const [activeTab, setActiveTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNotes, setEditingNotes] = useState({});
  const [isPushingUpdate, setIsPushingUpdate] = useState(false);
  const [botChatOpen, setBotChatOpen] = useState(false);

  // ── Batch mode state ──
  const [batchMode, setBatchMode] = useState(false);
  const [batchSelectedIds, setBatchSelectedIds] = useState(new Set());
  const [strategyNote, setStrategyNote] = useState('');
  const [batchBriefLoading, setBatchBriefLoading] = useState(false);
  const [batchBrief, setBatchBrief] = useState(null);
  const [batchBriefCopied, setBatchBriefCopied] = useState(false);
  const [batchBriefSaving, setBatchBriefSaving] = useState(false);
  const [batchVerifyLoading, setBatchVerifyLoading] = useState(false);
  const [batchVerifyMessage, setBatchVerifyMessage] = useState(null);
  const [batchVerifyProgress, setBatchVerifyProgress] = useState(null); // { current, total, bugTitle }

  // ── Sort state ──
  const [sortByDate, setSortByDate] = useState(true); // default: newest first

  // ── Pipeline launchpad state ──
  const [launchpad, setLaunchpad] = useState(null); // { run_id, feedback_id, title, domain, brief_id, project_url }

  // ── Project bundle ──
  const [bundleLoading, setBundleLoading] = useState(false);

  // ── Triage sweep state ──
  const [triageData, setTriageData] = useState(null);
  const [triageLoading, setTriageLoading] = useState(false);
  const [triageExpanded, setTriageExpanded] = useState(false);

  // ── Pre-flight modal state ──
  const [preflightData, setPreflightData] = useState(null); // { checks: [], item, hasBlocker, priorityOverride }

  // ── Dispatch password state ──
  const [dispatchPasswordOpen, setDispatchPasswordOpen] = useState(false);
  const [dispatchPassword, setDispatchPassword] = useState('');
  const [dispatchVerifying, setDispatchVerifying] = useState(false);
  const [dispatchError, setDispatchError] = useState('');
  const [launchpadModel, setLaunchpadModel] = useState('sonnet'); // opus or sonnet

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const isAdmin = user?.role === 'admin';

  React.useEffect(() => {
    if (user && !isAdmin) {
      navigate('/');
    }
  }, [user, isAdmin, navigate]);

  const { data: feedback = [], isLoading } = useQuery({
    queryKey: ['feedback'],
    queryFn: () => base44.entities.Feedback.list('-created_date'),
    enabled: isAdmin
  });

  const { data: officers = [] } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    enabled: isAdmin
  });

  const { data: fixBriefs = [] } = useQuery({
    queryKey: ['fixBriefs'],
    queryFn: () => base44.entities.FixBrief.list('-created_at'),
    enabled: isAdmin
  });

  const { data: testTargets = [] } = useQuery({
    queryKey: ['testTargets'],
    queryFn: () => base44.entities.TestTarget.list(),
    enabled: isAdmin
  });

  const { data: testRuns = [] } = useQuery({
    queryKey: ['testRuns'],
    queryFn: () => base44.entities.TestRun.list('-completed_at'),
    enabled: isAdmin
  });

  const { data: projectRegistry = [] } = useQuery({
    queryKey: ['projectRegistry'],
    queryFn: () => base44.entities.ProjectRegistry.list(),
    enabled: isAdmin
  });

  /* Stage 3: project_id -> project record map for fast lookup. */
  const projectById = useMemo(() => {
    var m = {};
    (projectRegistry || []).forEach(function(p) { m[p.id] = p; });
    return m;
  }, [projectRegistry]);
  const projectLabelOf = (item) => {
    var p = projectById[item?.project_id];
    return p ? (p.label || p.domain || '') : '';
  };

  const { data: pipelineRuns = [] } = useQuery({
    queryKey: ['pipelineRuns'],
    queryFn: () => base44.entities.PipelineRun.list('-created_at', 50),
    enabled: isAdmin,
    refetchInterval: 15000
  });

  // Domain staleness computation
  const domains = useMemo(() => {
    var domainMap = {};
    testTargets.forEach(function(t) {
      var d = t.domain || 'unknown';
      if (!domainMap[d]) domainMap[d] = { domain: d, label: d, total: 0, stale: 0 };
      domainMap[d].total++;
      var isTargetStale = !t.source_updated_at || (Date.now() - new Date(t.source_updated_at).getTime()) > 3 * 86400000;
      if (isTargetStale) domainMap[d].stale++;
    });
    // Attach labels from  ProjectRegistry
    projectRegistry.forEach(function(p) {
      if (domainMap[p.domain]) domainMap[p.domain].label = p.label || p.domain;
    });
    return Object.values(domainMap);
  }, [testTargets, projectRegistry]);

  const totalStale = domains.reduce(function(sum, d) { return sum + d.stale; }, 0);
  const isStale = totalStale > 0;

  // ── Pipeline helpers ──
  function findPipelineRun(feedbackId) {
    if (!feedbackId) return null;
    var fid = String(feedbackId);
    /* Find the most recent active or resolved run for this feedback */
    return pipelineRuns.find(function(r) { return r.feedback_id === fid; }) || null;
  }

  function getPipelineStageLabel(stage) {
    var labels = {
      briefing: { text: '📋 Generating brief...', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
      reading: { text: '📖 Reading source', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
      fixing: { text: '🔧 Fix in progress', color: 'bg-violet-500/10 text-violet-400 border-violet-500/20' },
      testing_pre: { text: '🧪 Pre-deploy testing', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
      awaiting_approval: { text: '⏳ Push ready — review', color: 'bg-amber-500/12 text-amber-300 border-amber-500/25 animate-pulse' },
      pushing: { text: '🚀 Pushing to GitHub', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
      syncing: { text: '⏳ Deploying...', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
      testing_post: { text: '🧪 Post-deploy testing', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
      resolved: { text: '✅ Verified', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
      failed: { text: '❌ Failed — retry available', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
      cancelled: { text: '⊘ Cancelled', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20' }
    };
    return labels[stage] || { text: stage, color: 'bg-slate-500/10 text-slate-400 border-slate-500/20' };
  }
  const [deployDropdown, setDeployDropdown] = useState(false);
  const [bundleDropdown, setBundleDropdown] = useState(false);
  const [markingDeployed, setMarkingDeployed] = useState(false);
  const [deployVerifyDialog, setDeployVerifyDialog] = useState(null); // { domain, bugs: [...] }
  const [deployVerifySelected, setDeployVerifySelected] = useState(new Set());

  const [selectedBriefId, setSelectedBriefId] = useState(null);

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Feedback.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['feedback'] })
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Feedback.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['feedback'] })
  });

  const getOfficerName = (email) => {
    const officer = officers.find(o => o.user_email === email);
    return officer ? officer.callsign : email;
  };

  const sendStatusChangeNotification = async (feedbackItem, newStatus) => {
    const oldStatus = feedbackItem.status;
    if (oldStatus === newStatus) return;

    const submitterProfile = officers.find(o => o.user_email === feedbackItem.submitted_by);
    const changerProfile = officers.find(o => o.user_email === user?.email);

    const statusLabels = { open: 'Open', needs_info: 'Needs Info', rejected: 'Rejected', promoted: 'Promoted', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };

    await base44.functions.invoke('sendNotification', {
      recipient_email: feedbackItem.submitted_by,
      type: 'feedback_status_change',
      source_type: 'feedback',
      source_id: feedbackItem.id,
      source_name: feedbackItem.title,
      triggered_by_email: user?.email,
      deep_link_params: { feedback_id: feedbackItem.id },
      metadata: {
        feedback_type: feedbackItem.type,
        feedback_title: feedbackItem.title,
        feedback_link: feedbackItem.link || null,
        feedback_description: feedbackItem.description || null,
        feedback_steps_to_reproduce: feedbackItem.steps_to_reproduce || null,
        feedback_expected_behavior: feedbackItem.expected_behavior || null,
        feedback_actual_behavior: feedbackItem.actual_behavior || null,
        feedback_attachments_count: feedbackItem.attachments?.length || 0,
        submitter_callsign: submitterProfile?.callsign || feedbackItem.submitted_by,
        previous_status: statusLabels[oldStatus] || oldStatus,
        new_status: statusLabels[newStatus] || newStatus,
        changed_by_name: changerProfile?.callsign || user?.email,
        admin_notes: feedbackItem.admin_notes || null,
        submitted_at: feedbackItem.created_date ? format(new Date(feedbackItem.created_date), 'MMM d, yyyy HH:mm') : 'unknown',
        changed_at: format(new Date(), 'MMM d, yyyy HH:mm')
      }
    });
  };

  const handleRequestInfo = async (feedbackItem, message) => {
    await base44.entities.Feedback.update(feedbackItem.id, { status: 'needs_info' });
    queryClient.invalidateQueries({ queryKey: ['feedback'] });

    const changerProfile = officers.find(o => o.user_email === user?.email);

    await base44.functions.invoke('sendNotification', {
      recipient_email: feedbackItem.submitted_by,
      type: 'feedback_needs_info',
      source_type: 'feedback',
      source_id: feedbackItem.id,
      source_name: feedbackItem.title,
      triggered_by_email: user?.email,
      deep_link_params: { feedback_id: feedbackItem.id },
      metadata: {
        feedback_type: feedbackItem.type,
        feedback_title: feedbackItem.title,
        feedback_link: feedbackItem.link || null,
        feedback_description: feedbackItem.description || null,
        feedback_steps_to_reproduce: feedbackItem.steps_to_reproduce || null,
        feedback_expected_behavior: feedbackItem.expected_behavior || null,
        feedback_actual_behavior: feedbackItem.actual_behavior || null,
        feedback_attachments_count: feedbackItem.attachments?.length || 0,
        info_request_message: message,
        requested_by_name: changerProfile?.callsign || user?.email
      },
      options: { skip_preferences_check: true }
    });
  };

  const handleRejectFeedback = async (feedbackItem, message) => {
    await base44.entities.Feedback.update(feedbackItem.id, {
      status: 'rejected',
      rejection_message: message,
      rejected_by: user?.email,
      rejected_at: new Date().toISOString()
    });
    queryClient.invalidateQueries({ queryKey: ['feedback'] });

    const changerProfile = officers.find(o => o.user_email === user?.email);
    await base44.functions.invoke('sendNotification', {
      recipient_email: feedbackItem.submitted_by,
      type: 'feedback_rejected',
      source_type: 'feedback',
      source_id: feedbackItem.id,
      source_name: feedbackItem.title,
      triggered_by_email: user?.email,
      deep_link_params: { feedback_id: feedbackItem.id },
      metadata: {
        feedback_type: feedbackItem.type,
        feedback_title: feedbackItem.title,
        rejection_message: message,
        rejected_by_name: changerProfile?.callsign || user?.email
      },
      options: { skip_preferences_check: true }
    });
  };

  const handlePromoteToIssue = async (feedbackItem, category) => {
    const newIssue = await base44.entities.Issue.create({
      title: feedbackItem.title,
      description: feedbackItem.description || feedbackItem.steps_to_reproduce || feedbackItem.actual_behavior || '',
      category: category,
      severity: feedbackItem.priority || 'medium',
      reported_by: feedbackItem.submitted_by,
      reported_at: feedbackItem.created_date || new Date().toISOString(),
      source: 'feedback',
      promoted_from_feedback_id: String(feedbackItem.id),
      status: 'open',
      classification_gate1: 'pending',
      classification_gate2: 'pending',
      frequency_signal: 'first_occurrence',
      observed_impact: feedbackItem.admin_notes || ''
    });

    await base44.entities.Feedback.update(feedbackItem.id, {
      status: 'promoted',
      promoted_to_issue_id: String(newIssue.id)
    });
    queryClient.invalidateQueries({ queryKey: ['feedback'] });
    queryClient.invalidateQueries({ queryKey: ['issues'] });

    const changerProfile = officers.find(o => o.user_email === user?.email);
    await base44.functions.invoke('sendNotification', {
      recipient_email: feedbackItem.submitted_by,
      type: 'feedback_promoted',
      source_type: 'feedback',
      source_id: feedbackItem.id,
      source_name: feedbackItem.title,
      triggered_by_email: user?.email,
      metadata: {
        feedback_title: feedbackItem.title,
        issue_category: category,
        promoted_by_name: changerProfile?.callsign || user?.email
      }
    }).catch(e => console.warn('Promotion notification failed:', e.message));
  };

  const handleUpdateStatus = async (item, newStatus) => {
    // ── For in_progress: defer EVERYTHING (notification + status) until pre-flight passes ──
    if (newStatus === 'in_progress' && item.status !== 'in_progress') {
      // Don't send notification yet — startPipeline will handle it
    } else {
      sendStatusChangeNotification(item, newStatus);
    }

    // ── Lifecycle event: status_changed ──
    var updatedLog = appendLifecycleEvent(item.lifecycle_log, 'status_changed', user?.email, {
      from: item.status,
      to: newStatus
    }, item.fix_cycle_count || 0);

    // ── For in_progress: defer status change until AFTER pre-flight passes ──
    if (newStatus === 'in_progress' && item.status !== 'in_progress') {
      // Don't mutate status yet — pre-flight modal will handle it via startPipeline
    } else {
      // ── Stage timestamps: idempotent — only record if not already set ──
      var existingTs = {};
      try { existingTs = (item.stage_timestamps && typeof item.stage_timestamps === 'object') ? item.stage_timestamps : {}; } catch(e) {}
      var newTs = Object.assign({}, existingTs);
      if (!newTs['open']) newTs['open'] = item.created_date || new Date().toISOString();
      if (!newTs[newStatus]) newTs[newStatus] = new Date().toISOString();
      updateMutation.mutate({ id: item.id, data: { status: newStatus, lifecycle_log: updatedLog, stage_timestamps: newTs } });
    }

    // ── Move 2: Accept & Start pipeline when moving to in_progress ──
    if (newStatus === 'in_progress' && item.status !== 'in_progress') {

      // ── Pre-flight check: consume enrichment intelligence before proceeding ──
      var checks = [];

      // Check 1: Is this item enriched? Is enrichment current?
      if (!item.enriched_at) {
        checks.push({ id: 'enrich', level: 'warn', label: 'Not enriched', msg: 'No AI enrichment yet. Cluster, complexity, duplicate detection, and priority scoring are missing.', action: 'Enrich first' });
      } else if (!item.priority_score || !item.ai_summary) {
        checks.push({ id: 'enrich_stale', level: 'warn', label: 'Enrichment outdated', msg: 'Enriched but missing priority score or AI summary. Re-enrich to get full analysis.', action: 'Re-enrich' });
      }

      // Check 2: Duplicate detection
      if (item.enrichment_status === 'enriched' && item.possible_duplicate_of) {
        var dupItem = feedback.find(function(f) { return String(f.id) === String(item.possible_duplicate_of); });
        var dupTitle = dupItem ? dupItem.title : '#' + item.possible_duplicate_of;
        var dupResolved = dupItem && (dupItem.status === 'resolved' || dupItem.status === 'closed');
        if (dupResolved) {
          checks.push({ id: 'dup_resolved', level: 'block', label: 'Duplicate (resolved)', msg: 'Possible duplicate of "' + dupTitle + '" which is ALREADY RESOLVED.', action: 'Close as duplicate' });
        } else {
          checks.push({ id: 'dup_open', level: 'warn', label: 'Possible duplicate', msg: 'Flagged as possible duplicate of "' + dupTitle + '".', action: 'View duplicate' });
        }
      }

      // Check 3: Staleness
      var itemAgeDays = item.created_date ? (Date.now() - new Date(item.created_date).getTime()) / 86400000 : 0;
      if (itemAgeDays > 14 && item.complexity === 'trivial') {
        checks.push({ id: 'stale', level: 'warn', label: 'Stale (trivial)', msg: 'This trivial item is ' + Math.round(itemAgeDays) + ' days old.', action: 'Mark stale' });
      } else if (itemAgeDays > 30) {
        checks.push({ id: 'stale', level: 'warn', label: 'Aging (' + Math.round(itemAgeDays) + 'd)', msg: 'Submitted ' + Math.round(itemAgeDays) + ' days ago. Confirm still relevant.' });
      } else {
        checks.push({ id: 'age', level: 'pass', label: 'Age: ' + itemAgeDays.toFixed(1) + 'd', msg: 'Within normal range.' });
      }

      // Check 4: Cluster siblings resolved?
      if (item.cluster_id) {
        var resolvedSiblings = feedback.filter(function(f) {
          return f.cluster_id === item.cluster_id && String(f.id) !== String(item.id) && (f.status === 'resolved' || f.status === 'closed');
        });
        var activeSiblings = feedback.filter(function(f) {
          return f.cluster_id === item.cluster_id && String(f.id) !== String(item.id) && f.status !== 'resolved' && f.status !== 'closed';
        });
        if (resolvedSiblings.length > 0) {
          checks.push({ id: 'cluster_resolved', level: 'warn', label: 'Cluster has fixes', msg: resolvedSiblings.length + ' sibling(s) in "' + item.cluster_id + '" already resolved. May be fixed.', action: 'View cluster' });
        }
        checks.push({ id: 'cluster', level: 'info', label: 'Cluster: ' + item.cluster_id, msg: (activeSiblings.length + 1) + ' active, ' + resolvedSiblings.length + ' resolved in this cluster.' });
      }

      // Check 5: Priority score
      if (item.priority_score) {
        var scoreLabel = item.priority_score > 100 ? 'Critical' : item.priority_score > 50 ? 'High' : item.priority_score > 20 ? 'Moderate' : 'Low';
        checks.push({ id: 'priority', level: item.priority_score < 10 ? 'warn' : 'info', label: 'Priority: ' + item.priority_score + ' (' + scoreLabel + ')', msg: 'AI base × frequency × age decay' + (item.priority_override ? ' × override: ' + item.priority_override : '') });
      } else {
        checks.push({ id: 'priority', level: 'info', label: 'Priority: not scored', msg: 'Run enrichment to compute priority score.' });
      }

      // Check 6: Complexity
      if (item.complexity) {
        checks.push({ id: 'complexity', level: item.complexity === 'complex' ? 'warn' : 'pass', label: 'Complexity: ' + item.complexity, msg: item.complexity === 'complex' ? 'Multi-file change expected. May need multiple iterations.' : item.complexity === 'trivial' ? 'Simple fix expected.' : 'Standard single-function fix.' });
      }

      // Check 7: Source app — derive from project_id (Stage 3: dead source_app/source_app_name fields removed).
      var srcApp = projectLabelOf(item) || 'Compass';
      checks.push({ id: 'source', level: 'pass', label: 'Source: ' + srcApp, msg: 'Will route to ' + srcApp + ' repository.' });

      // Check 8: AI Summary
      if (item.ai_summary) {
        checks.push({ id: 'summary', level: 'info', label: 'AI summary', msg: item.ai_summary });
      }

      // Open the pre-flight modal instead of confirm()
      setPreflightData({
        checks: checks,
        item: item,
        hasBlocker: checks.some(function(c) { return c.level === 'block'; }),
        priorityOverride: item.priority_override || '',
        newStatus: newStatus,
        updatedLog: updatedLog
      });
      return; // Don't proceed yet — modal handles continuation
    }
  };

  // ── Pipeline creation (called from pre-flight modal "Proceed" button) ──
  // COMPASS provides structured context. Claude.ai generates its own brief from context + code.
  const startPipeline = async (item, updatedLog) => {
      // Set status to in_progress (deferred from handleUpdateStatus)
      var existingTs2 = {};
      try { existingTs2 = (item.stage_timestamps && typeof item.stage_timestamps === 'object') ? item.stage_timestamps : {}; } catch(e) {}
      var newTs2 = Object.assign({}, existingTs2);
      if (!newTs2['open']) newTs2['open'] = item.created_date || new Date().toISOString();
      if (!newTs2['in_progress']) newTs2['in_progress'] = new Date().toISOString();
      updateMutation.mutate({ id: item.id, data: { status: 'in_progress', lifecycle_log: updatedLog, stage_timestamps: newTs2 } });

      // Send notification (deferred from handleUpdateStatus)
      sendStatusChangeNotification(item, 'in_progress');

      var project = projectById[item.project_id] || null;
      var projectDomain = project?.domain || '';      var pipelineRunId = 'PR-' + new Date().toISOString().replace(/[-:T]/g, '').substring(0, 14);

      // Create PipelineRun
      try {
        await base44.entities.PipelineRun.create({
          run_id: pipelineRunId,
          feedback_id: String(item.id),
          brief_id: '',
          project_domain: projectDomain,
          entry_point: 'compass',
          stage: 'dispatching',
          target_files: [],
          commit_sha: '',
          test_run_id_pre: '',
          test_run_id_post: '',
          attempt: (item.fix_cycle_count || 0) + 1,
          failure_context: '',
          diff_summary: '',
          started_at: new Date().toISOString(),
          completed_at: '',
          started_by: user?.email || 'admin'
        });
        queryClient.refetchQueries({ queryKey: ['pipelineRuns'] });
        console.log('[PIPELINE] PipelineRun ' + pipelineRunId + ' created for ' + item.id);
      } catch (pipeErr) {
        console.warn('[PIPELINE] Failed to create PipelineRun:', pipeErr.message);
      }

      // Fetch model recommendation
      var modelRec = { model: 'sonnet', reasons: [] };
      try {
        var mrResult = await base44.functions.invoke('pipelineApi', { action: 'model:recommend', feedback_id: String(item.id) });
        modelRec = mrResult?.data || mrResult || modelRec;
      } catch(e) { /* default to sonnet */ }

      // Copy command to clipboard
      var pipelineMode = item.type === 'bug' ? 'fix' : 'build';
      var pipelineCommand = '/pipeline ' + pipelineMode + ' ' + String(item.id);
      try { await navigator.clipboard.writeText(pipelineCommand); } catch(e) {}

      // Show Launchpad immediately — no brief generation, no timeout
      var projectUrl = project?.claude_project_url || '';
      setLaunchpad({
        run_id: pipelineRunId,
        feedback_id: String(item.id),
        title: item.title,
        domain: projectDomain,
        brief_id: '',
        project_url: projectUrl,
        command: pipelineCommand,
        model: modelRec.model || 'sonnet',
        modelReasons: modelRec.reasons || []
      });
      setLaunchpadModel(modelRec.model || 'sonnet');

      console.log('[PIPELINE] ' + pipelineRunId + ' dispatching → Launchpad open');
  };

  const handleUpdatePriority = (id, priority) => {
    updateMutation.mutate({ id, data: { priority } });
  };

  const handleUpdateAll = async (id, data) => {
    await base44.entities.Feedback.update(id, data);
    queryClient.invalidateQueries({ queryKey: ['feedback'] });
  };

  const handleSaveNotes = (id) => {
    if (editingNotes[id] !== undefined) {
      updateMutation.mutate({ id, data: { admin_notes: editingNotes[id] } });
      setEditingNotes(prev => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    }
  };

  // ── Brief helpers ──
  const findExistingBrief = (bugId) => {
    var idStr = String(bugId);
    return fixBriefs.find(function(b) {
      return Array.isArray(b.bug_ids) && b.bug_ids.indexOf(idStr) !== -1;
    }) || null;
  };

  const handleOpenBrief = (briefId) => {
    setSelectedBriefId(briefId);
    setPageMode('briefs');
  };

  const handlePushUpdate = async () => {
    const version = prompt('Enter version number (e.g., 2.0.5):');
    if (!version) return;
    const releaseNotes = prompt('Enter release notes (optional):') || '';
    setIsPushingUpdate(true);
    try {
      await base44.functions.invoke('notifyAppUpdate', { version, releaseNotes });
      alert('Update notification sent to all users!');
    } catch (error) {
      alert('Failed to send notification: ' + error.message);
    } finally {
      setIsPushingUpdate(false);
    }
  };

  const handleDeployAndVerifyOpen = async (domain) => {
    setDeployDropdown(false);
    // Find in_progress bugs that might be covered by this deployment
    var inProgressBugs = feedback.filter(function(f) { return f.status === 'in_progress'; });
    // Only pre-select bugs that have a brief ready (work was actually done)
    var briefReadyIds = new Set(inProgressBugs.filter(function(f) {
      return f.latest_brief_id || fixBriefs.some(function(fb) { return Array.isArray(fb.bug_ids) && fb.bug_ids.indexOf(String(f.id)) !== -1; });
    }).map(function(f) { return f.id; }));
    setDeployVerifySelected(briefReadyIds);
    setDeployVerifyDialog({ domain: domain, bugs: inProgressBugs });
  };

  const handleDeployAndVerifyConfirm = async () => {
    if (!deployVerifyDialog) return;
    var domain = deployVerifyDialog.domain;
    var selectedBugIds = Array.from(deployVerifySelected);
    setDeployVerifyDialog(null);
    setMarkingDeployed(true);

    try {
      // Step 1: Mark targets as deployed
      var result = await base44.functions.invoke('testerCasterSeedTests', {
        action: 'mark_deployed',
        all: true,
        domain: domain || ''
      });
      var data = result?.data || result;
      queryClient.invalidateQueries({ queryKey: ['testTargets'] });

      // Step 2: Create TestRuns for each selected bug
      var runsCreated = 0;
      for (var i = 0; i < selectedBugIds.length; i++) {
        var bugId = selectedBugIds[i];
        var bugItem = feedback.find(function(f) { return f.id === bugId; });
        if (!bugItem) continue;

        try {
          var fixDesc = bugItem.admin_notes || bugItem.description || bugItem.title;
          // Infer target
          var inferResult = await base44.functions.invoke('testerCasterSeedTests', {
            action: 'infer_target',
            bug_title: bugItem.title,
            bug_description: fixDesc
          });
          var infer = inferResult?.data || inferResult;
          if (!infer.target_id || infer.target_id === 'unknown' || infer.api_testable === false) continue;

          // Generate acceptance test
          var acceptanceTestId = null;
          try {
            var acceptResult = await base44.functions.invoke('testerCasterSeedTests', {
              action: 'generate_acceptance_test',
              bug_id: bugId,
              target_id: infer.target_id
            });
            var accept = acceptResult?.data || acceptResult;
            if (!accept.skipped && accept.acceptance_test) {
              var prepared = Object.assign({}, accept.acceptance_test);
              if (prepared.input_sequence && typeof prepared.input_sequence !== 'string') {
                prepared.input_sequence = JSON.stringify(prepared.input_sequence);
              }
              if (prepared.assertions && typeof prepared.assertions !== 'string') {
                prepared.assertions = JSON.stringify(prepared.assertions);
              }
              await base44.entities.TestCase.create(prepared);
              acceptanceTestId = accept.acceptance_test.test_id;
            }
          } catch (e) { console.warn('Acceptance gen failed:', e.message); }

          // Select regression tests
          var regressionIds = [];
          if (infer.has_library && infer.test_count > 0) {
            try {
              var selectResult = await base44.functions.invoke('testerCasterSeedTests', {
                action: 'select_tests',
                fix_description: fixDesc,
                bug_title: bugItem.title,
                target: infer.target_id
              });
              regressionIds = (selectResult?.data || selectResult).test_ids || [];
            } catch (e) {}
          }

          var allTestIds = acceptanceTestId ? [acceptanceTestId].concat(regressionIds) : regressionIds;
          if (allTestIds.length === 0) continue;

          var runId = 'TR-' + new Date().toISOString().split('T')[0].replace(/-/g, '') + '-' + String(Date.now()).slice(-4);
          await base44.entities.TestRun.create({
            run_id: runId,
            bug_id: String(bugId),
            requested_by: user?.email || 'unknown',
            assigned_to: 'TesterCaster',
            status: 'pending',
            fix_description: fixDesc + '\n\n[Deploy & Verify | Target: ' + infer.target_name + ' | Acceptance: ' + (acceptanceTestId ? 1 : 0) + ' | Regression: ' + regressionIds.length + ']',
            test_ids: allTestIds,
            acceptance_test_ids: acceptanceTestId ? [acceptanceTestId] : [],
            layers: ['API'],
            loop_count: 1,
            max_loops: 3,
            run_created_at: new Date().toISOString()
          });

          // Write lifecycle event: deployed + verify_started
          var deployLog = appendLifecycleEvent(bugItem.lifecycle_log, 'deployed', user?.email, {
            domain: domain, targets: [infer.target_name]
          }, bugItem.fix_cycle_count || 0);
          deployLog = appendLifecycleEvent(deployLog, 'verify_started', 'system', {
            run_id: runId, test_count: allTestIds.length
          }, bugItem.fix_cycle_count || 0);
          await base44.entities.Feedback.update(bugId, {
            latest_run_id: runId,
            latest_run_status: 'pending',
            lifecycle_log: deployLog
          });

          runsCreated++;
        } catch (e) {
          console.warn('Deploy & Verify failed for bug ' + bugId + ':', e.message);
        }
      }

      queryClient.refetchQueries({ queryKey: ['feedback'] });
      queryClient.refetchQueries({ queryKey: ['testRuns'] });
      alert((data.updated || 0) + ' targets deployed. ' + runsCreated + ' verification runs created.');
    } catch (err) {
      alert('Deploy & Verify failed: ' + err.message);
    } finally {
      setMarkingDeployed(false);
    }
  };

  const handleExportBundle = async (domain) => {
    setBundleDropdown(false);
    setBundleLoading(true);
    try {
      var result = await base44.functions.invoke('testerCasterSeedTests', {
        action: 'generate_project_bundle',
        domain: domain || ''
      });
      var data = result?.data || result;
      if (data.error) {
        alert('Bundle error: ' + data.error);
      } else if (data.bundle) {
        navigator.clipboard.writeText(data.bundle).then(function() {
          alert('Bundle copied! (' + data.target_count + ' targets, ' + data.test_count + ' tests, ' + data.stale_targets + ' stale)' + (domain ? '\nDomain: ' + domain : '\nAll domains') + '\n\nPaste into your Claude Project as a knowledge file.');
        });
      }
    } catch (err) {
      alert('Bundle failed: ' + err.message);
    } finally {
      setBundleLoading(false);
    }
  };

  // ── Batch handlers ──
  const toggleBatchSelect = (id) => {
    setBatchSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exitBatchMode = () => {
    setBatchMode(false);
    setBatchSelectedIds(new Set());
    setStrategyNote('');
    setBatchBrief(null);
    setBatchVerifyMessage(null);
  };

  const handleBatchBrief = async () => {
    const ids = Array.from(batchSelectedIds);
    if (ids.length === 0) return;
    setBatchBriefLoading(true);
    setBatchBrief(null);
    try {
      var result = await base44.functions.invoke('testerCasterSeedTests', {
        action: 'generate_batch_brief',
        bug_ids: ids,
        strategy_note: strategyNote || ''
      });
      var data = result?.data || result;
      if (data.error) {
        setBatchVerifyMessage({ type: 'error', text: 'Batch Brief failed: ' + data.error });
      } else if (data.brief) {
        setBatchBrief(data.brief);
      }
    } catch (err) {
      setBatchVerifyMessage({ type: 'error', text: 'Batch Brief error: ' + err.message });
    } finally {
      setBatchBriefLoading(false);
    }
  };

  const handleClusterBatchBrief = async (clusterId) => {
    // Find all bugs in this cluster
    var clusterBugs = feedback.filter(function(f) { return f.cluster_id === clusterId; });
    if (clusterBugs.length === 0) return;

    var ids = clusterBugs.map(function(f) { return f.id; });
    setBatchBriefLoading(true);
    setBatchBrief(null);
    setBatchMode(true);
    setBatchSelectedIds(new Set(ids));
    setStrategyNote('Cluster: ' + clusterId);

    try {
      var result = await base44.functions.invoke('testerCasterSeedTests', {
        action: 'generate_batch_brief',
        bug_ids: ids,
        strategy_note: 'Cluster: ' + clusterId + ' (' + clusterBugs.length + ' items)'
      });
      var data = result?.data || result;
      if (data.error) {
        setBatchVerifyMessage({ type: 'error', text: 'Cluster Batch Brief failed: ' + data.error });
      } else if (data.brief) {
        setBatchBrief(data.brief);
        // Auto-save as FixBrief entity
        var briefId = 'BR-CLU-' + new Date().toISOString().split('T')[0].replace(/-/g, '') + '-' + String(Date.now()).slice(-4);
        try {
          await base44.entities.FixBrief.create({
            brief_id: briefId,
            brief_text: data.brief,
            bug_ids: ids.map(String),
            strategy_note: 'Cluster: ' + clusterId,
            source: 'batch',
            target_names: [],
            created_by: user?.email || 'unknown',
            created_at: new Date().toISOString()
          });
          // Update each bug with the brief reference
          for (var i = 0; i < ids.length; i++) {
            try {
              await base44.entities.Feedback.update(ids[i], { latest_brief_id: briefId });
            } catch (e) {}
          }
          queryClient.refetchQueries({ queryKey: ['feedback'] });
          queryClient.refetchQueries({ queryKey: ['fixBriefs'] });
        } catch (e) { console.warn('Cluster brief save failed:', e.message); }
      }
    } catch (err) {
      setBatchVerifyMessage({ type: 'error', text: 'Cluster Batch Brief error: ' + err.message });
    } finally {
      setBatchBriefLoading(false);
    }
  };

  const handleCopyBatchBrief = () => {
    if (!batchBrief) return;
    navigator.clipboard.writeText(batchBrief).then(function() {
      setBatchBriefCopied(true);
      setTimeout(function() { setBatchBriefCopied(false); }, 2000);
    });
  };

  const handleOpenBatchBriefAsPage = async () => {
    if (!batchBrief) return;
    setBatchBriefSaving(true);
    try {
      var briefId = 'BR-' + new Date().toISOString().split('T')[0].replace(/-/g, '') + '-' + String(Date.now()).slice(-4);
      var ids = Array.from(batchSelectedIds).map(function(id) { return String(id); });
      // Collect target names from selected bugs
      var selectedBugs = feedback.filter(function(f) { return batchSelectedIds.has(f.id); });
      var targetNames = [];
      selectedBugs.forEach(function(b) {
        var tags = b.tags || [];
        tags.forEach(function(t) { if (targetNames.indexOf(t) === -1) targetNames.push(t); });
      });
      await base44.entities.FixBrief.create({
        brief_id: briefId,
        brief_text: batchBrief,
        bug_ids: ids,
        strategy_note: strategyNote || '',
        source: 'batch',
        target_names: targetNames,
        created_by: user?.email || 'unknown',
        created_at: new Date().toISOString()
      });
      handleOpenBrief(briefId);
    } catch (err) {
      console.error('Failed to save batch brief:', err);
    } finally {
      setBatchBriefSaving(false);
    }
  };

  const handleBatchVerify = async () => {
    const ids = Array.from(batchSelectedIds);
    if (ids.length === 0) return;
    setBatchVerifyLoading(true);
    setBatchVerifyMessage(null);
    try {
      // Step 1: Infer targets for all bugs + generate acceptance tests
      var acceptanceTestIds = [];
      var allRegressionIds = [];
      var targetNames = [];
      var skippedBugs = [];

      for (var i = 0; i < ids.length; i++) {
        var bugId = ids[i];
        var bugItem = feedback.find(function(f) { return f.id === bugId; });
        var fixDesc = bugItem ? (bugItem.admin_notes || bugItem.description || bugItem.title) : '';
        var bugTitle = bugItem ? bugItem.title : 'Bug #' + bugId;

        // Progress update
        setBatchVerifyProgress({ current: i + 1, total: ids.length, bugTitle: bugTitle, phase: 'Inferring target' });

        // Infer target
        var inferResult = await base44.functions.invoke('testerCasterSeedTests', {
          action: 'infer_target',
          bug_title: bugItem ? bugItem.title : '',
          bug_description: fixDesc
        });
        var infer = inferResult?.data || inferResult;

        if (!infer.target_id || infer.target_id === 'unknown' || infer.api_testable === false) {
          skippedBugs.push(bugItem ? bugItem.title : bugId);
          continue;
        }

        if (targetNames.indexOf(infer.target_name) === -1) targetNames.push(infer.target_name);

        // Progress update
        setBatchVerifyProgress({ current: i + 1, total: ids.length, bugTitle: bugTitle, phase: 'Generating acceptance test' });

        // Generate acceptance test
        try {
          var acceptResult = await base44.functions.invoke('testerCasterSeedTests', {
            action: 'generate_acceptance_test',
            bug_id: bugId,
            target_id: infer.target_id
          });
          var accept = acceptResult?.data || acceptResult;
          if (!accept.skipped && accept.acceptance_test) {
            // Seed the acceptance test
            var prepared = Object.assign({}, accept.acceptance_test);
            if (prepared.input_sequence && typeof prepared.input_sequence !== 'string') {
              prepared.input_sequence = JSON.stringify(prepared.input_sequence);
            }
            if (prepared.assertions && typeof prepared.assertions !== 'string') {
              prepared.assertions = JSON.stringify(prepared.assertions);
            }
            await base44.entities.TestCase.create(prepared);
            acceptanceTestIds.push(accept.acceptance_test.test_id);
          }
        } catch(e) {
          console.warn('Acceptance gen failed for bug ' + bugId + ':', e.message);
        }

        // Select regression tests (if library exists)
        if (infer.has_library && infer.test_count > 0) {
          try {
            var selectResult = await base44.functions.invoke('testerCasterSeedTests', {
              action: 'select_tests',
              fix_description: fixDesc,
              bug_title: bugItem ? bugItem.title : '',
              target: infer.target_id
            });
            var selection = selectResult?.data || selectResult;
            (selection.test_ids || []).forEach(function(tid) {
              if (allRegressionIds.indexOf(tid) === -1) allRegressionIds.push(tid);
            });
          } catch(e) {}
        }
      }

      // Step 2: Combine and create TestRun
      var allTestIds = acceptanceTestIds.concat(allRegressionIds);
      if (allTestIds.length === 0) {
        setBatchVerifyMessage({ type: 'warn', text: 'No testable bugs in batch. ' + (skippedBugs.length > 0 ? 'Skipped: ' + skippedBugs.join(', ') : '') });
        setBatchVerifyLoading(false);
        return;
      }

      var newRunId = 'TR-' + new Date().toISOString().split('T')[0].replace(/-/g, '') + '-' +
        String(Date.now()).slice(-4);

      await base44.entities.TestRun.create({
        run_id: newRunId,
        bug_id: ids.join(','),
        requested_by: user?.email || 'unknown',
        assigned_to: 'TesterCaster',
        status: 'pending',
        fix_description: 'BATCH VERIFY (' + ids.length + ' bugs)\n' + (strategyNote ? 'Strategy: ' + strategyNote + '\n' : '') + '[Targets: ' + targetNames.join(', ') + ' | Acceptance: ' + acceptanceTestIds.length + ' | Regression: ' + allRegressionIds.length + ']',
        test_ids: allTestIds,
        acceptance_test_ids: acceptanceTestIds,
        layers: ['API'],
        loop_count: 1,
        max_loops: 3,
        run_created_at: new Date().toISOString()
      });

      var parts = [];
      if (acceptanceTestIds.length > 0) parts.push(acceptanceTestIds.length + ' acceptance');
      if (allRegressionIds.length > 0) parts.push(allRegressionIds.length + ' regression');
      if (skippedBugs.length > 0) parts.push(skippedBugs.length + ' skipped');
      setBatchVerifyMessage({ type: 'success', text: 'Batch run created: ' + parts.join(' + ') + ' tests queued (' + newRunId + ')' });
      setTimeout(function() { setBatchVerifyMessage(null); }, 8000);
    } catch (err) {
      setBatchVerifyMessage({ type: 'error', text: 'Batch verify failed: ' + err.message });
    } finally {
      setBatchVerifyLoading(false);
      setBatchVerifyProgress(null);
    }
  };

  // Filtering logic
  const filteredFeedback = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return feedback.filter(item => {
      if (activeTab !== 'all' && item.type !== activeTab) return false;
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && (item.priority || 'medium') !== priorityFilter) return false;
      if (query) {
        const searchableText = [
          item.title,
          item.description,
          item.steps_to_reproduce,
          item.expected_behavior,
          item.actual_behavior,
          item.admin_notes,
          item.submitted_by,
          getOfficerName(item.submitted_by),
          projectLabelOf(item),
          item.cluster_id,
          item.complexity,
          item.ai_summary,
          item.priority,
          String(item.id),
          Array.isArray(item.tags) ? item.tags.join(' ') : ''
        ].filter(Boolean).join(' ').toLowerCase();
        if (!searchableText.includes(query)) return false;
      }
      return true;
    });
  }, [feedback, activeTab, statusFilter, priorityFilter, searchQuery, officers]);

  const bugCount = feedback.filter(f => f.type === 'bug').length;
  const featureCount = feedback.filter(f => f.type === 'feature').length;

  // ── Kanban state ──
  const [selectedItemId, setSelectedItemId] = useState(null);

  /* Auto-open feedback detail panel from notification deep links (?highlight=feedbackId) */
  React.useEffect(() => {
    try {
      var params = new URLSearchParams(window.location.search);
      var highlightId = params.get('highlight');
      if (highlightId) {
        setSelectedItemId(highlightId);
        /* Clean URL without reload */
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch(e) {}
  }, []);
  const [resolvedLimit, setResolvedLimit] = useState(20);
  const [expandedKPI, setExpandedKPI] = useState(null);
  const selectedItem = feedback.find(f => f.id === selectedItemId) || null;

  // ── Column computation ──
  const columns = useMemo(() => {
    var q = searchQuery.toLowerCase();
    var filtered = feedback.filter(function(item) {
      if (activeTab !== 'all' && item.type !== activeTab) return false;
      if (q) {
        var text = [item.title, item.description, item.steps_to_reproduce, item.expected_behavior, item.actual_behavior, item.admin_notes, item.submitted_by, getOfficerName(item.submitted_by), projectLabelOf(item), item.cluster_id, item.complexity, item.ai_summary, item.priority, String(item.id), Array.isArray(item.tags) ? item.tags.join(' ') : ''].filter(Boolean).join(' ').toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });

    var needsReview = [];
    var inProgress = [];
    var verifying = [];
    var resolved = [];
    var stuck = [];

    filtered.forEach(function(item) {
      // Check for active test run
      var run = testRuns.find(function(r) { return String(r.bug_id) === String(item.id); });
      var hasActiveRun = run && (run.status === 'pending' || run.status === 'running');
      var hasFailed = run && run.status === 'failed';

      if (item.status === 'resolved' || item.status === 'closed') {
        resolved.push(item);
      } else if (item.status === 'needs_info' || item.status === 'rejected' || item.status === 'promoted') {
        stuck.push(item);
      } else if (item.status === 'in_progress' && hasFailed) {
        stuck.push(item);
      } else if (item.status === 'in_progress' && hasActiveRun) {
        verifying.push(item);
      } else if (item.status === 'in_progress') {
        inProgress.push(item);
      } else {
        needsReview.push(item);
      }
    });

    return { needsReview, inProgress, verifying, resolved, stuck };
  }, [feedback, testRuns, activeTab, searchQuery, officers]);

  // ── Cluster grouping helper ──
  function groupByCluster(items) {
    var clusters = {};
    var solo = [];
    items.forEach(function(item) {
      if (item.cluster_id) {
        if (!clusters[item.cluster_id]) clusters[item.cluster_id] = [];
        clusters[item.cluster_id].push(item);
      } else {
        solo.push(item);
      }
    });
    // Only group clusters with 2+ items
    var groups = [];
    Object.keys(clusters).forEach(function(cid) {
      if (clusters[cid].length >= 2) {
        groups.push({ type: 'cluster', cluster_id: cid, items: clusters[cid] });
      } else {
        solo.push(clusters[cid][0]);
      }
    });
    solo.forEach(function(item) { groups.push({ type: 'solo', items: [item] }); });

    // Sort groups by date if enabled
    if (sortByDate) {
      var getGroupDate = function(group) {
        // For clusters, use the most recent card's date
        var dates = group.items.map(function(item) {
          return new Date(item.created_date || item.updated_date || 0).getTime();
        });
        return Math.max.apply(null, dates);
      };
      groups.sort(function(a, b) { return getGroupDate(b) - getGroupDate(a); });
      // Also sort items within clusters by date (newest first)
      groups.forEach(function(group) {
        if (group.type === 'cluster') {
          group.items.sort(function(a, b) {
            return new Date(b.created_date || 0).getTime() - new Date(a.created_date || 0).getTime();
          });
        }
      });
    }

    return groups;
  }

  // ── Inline next step indicator ──
  function getNextStep(item) {
    var run = testRuns.find(function(r) { return String(r.bug_id) === String(item.id); });
    var pr = findPipelineRun(item.id);
    if (item.status === 'rejected') return { text: '↩️ Returned', c: 'rgba(255,82,82,.08)', tc: '#FF8A80', bc: 'rgba(255,82,82,.15)' };
    if (item.status === 'promoted') return { text: '⬆️ In High Council', c: 'rgba(108,92,231,.08)', tc: '#A5B4FC', bc: 'rgba(108,92,231,.15)' };
    if (item.status === 'needs_info') return { text: '⏸️ Waiting for submitter', c: 'rgba(255,215,64,.06)', tc: '#FFE57F', bc: 'rgba(255,215,64,.12)' };
    if (item.status === 'resolved') return { text: '✅ Verified', c: 'rgba(0,230,118,.06)', tc: '#69F0AE', bc: 'rgba(0,230,118,.12)' };
    if (item.enrichment_status === 'pending') return { text: '⏳ Enriching...', c: 'rgba(107,113,144,.06)', tc: '#8B8FA3', bc: 'rgba(107,113,144,.12)' };
    if (item.status === 'open') {
      if (item.possible_duplicate_of) return { text: '⚠️ Possible dup of #' + String(item.possible_duplicate_of).slice(-6), c: 'rgba(255,215,64,.06)', tc: '#FFE57F', bc: 'rgba(255,215,64,.12)' };
      return { text: '👀 Review → accept or reject', c: 'rgba(0,210,255,.06)', tc: 'rgba(0,210,255,.8)', bc: 'rgba(0,210,255,.12)' };
    }
    if (item.status === 'in_progress') {
      /* Pipeline stage takes priority when a PipelineRun exists */
      if (pr && pr.stage !== 'resolved' && pr.stage !== 'cancelled') {
        var stageMap = {
          briefing: { text: '📋 Generating brief...', c: 'rgba(217,119,6,.06)', tc: '#FBBF24', bc: 'rgba(217,119,6,.12)' },
          reading: { text: '📖 Reading source...', c: 'rgba(37,99,235,.06)', tc: '#60A5FA', bc: 'rgba(37,99,235,.12)' },
          fixing: { text: '🔧 Fix in progress', c: 'rgba(139,92,246,.06)', tc: '#B388FF', bc: 'rgba(139,92,246,.12)' },
          testing_pre: { text: '🧪 Pre-deploy testing', c: 'rgba(13,148,136,.06)', tc: '#5EEAD4', bc: 'rgba(13,148,136,.12)' },
          awaiting_approval: { text: '⏳ Brief ready → open claude.ai', c: 'rgba(217,119,6,.08)', tc: '#FBBF24', bc: 'rgba(217,119,6,.15)' },
          pushing: { text: '🚀 Pushing to GitHub', c: 'rgba(37,99,235,.06)', tc: '#60A5FA', bc: 'rgba(37,99,235,.12)' },
          syncing: { text: '⏳ Deploying...', c: 'rgba(37,99,235,.06)', tc: '#60A5FA', bc: 'rgba(37,99,235,.12)' },
          testing_post: { text: '🧪 Post-deploy testing', c: 'rgba(13,148,136,.06)', tc: '#5EEAD4', bc: 'rgba(13,148,136,.12)' },
          failed: { text: '❌ Pipeline failed — retry', c: 'rgba(255,82,82,.06)', tc: '#FF8A80', bc: 'rgba(255,82,82,.12)' }
        };
        return stageMap[pr.stage] || { text: '🔧 Pipeline active', c: 'rgba(139,92,246,.06)', tc: '#B388FF', bc: 'rgba(139,92,246,.12)' };
      }
      /* Fallback: no pipeline run, use legacy indicators */
      if (run && (run.status === 'pending' || run.status === 'running')) return { text: '🧪 Verifying...', c: 'rgba(179,136,255,.06)', tc: '#B388FF', bc: 'rgba(179,136,255,.12)' };
      if (run && run.status === 'failed') return { text: '❌ Cycle ' + (item.fix_cycle_count || 1) + ' failed', c: 'rgba(255,82,82,.06)', tc: '#FF8A80', bc: 'rgba(255,82,82,.12)' };
      if (item.latest_brief_id || findExistingBrief(item.id)) return { text: '📋 Brief ready → fix → deploy', c: 'rgba(139,92,246,.06)', tc: '#B388FF', bc: 'rgba(139,92,246,.12)' };
      return { text: '📋 Generating brief...', c: 'rgba(139,92,246,.04)', tc: 'rgba(179,136,255,.6)', bc: 'rgba(139,92,246,.08)' };
    }
    return null;
  }

  if (!isAdmin) return null;

  // ── Source app colors ──
  var APP_COLORS = { 'COMPASS': '#6366f1', 'Schedulix': '#f59e0b', 'Schedule Optimizer': '#f59e0b', 'Ops Dashboard': '#22c55e', 'Purchaser': '#06b6d4' };
  var PIPELINE_STAGES = ['briefing','reading','fixing','testing_pre','awaiting_approval','pushing','syncing','testing_post','resolved'];

  // ── Render helpers ──
  const renderCard = (item) => {
    var step = getNextStep(item);
    var isBug = item.type === 'bug';
    var isSelected = selectedItemId === item.id;
    var pr = findPipelineRun(item.id);
    var hasBrief = item.latest_brief_id || findExistingBrief(item.id);

    // Age calculation for left bar color
    var ageDays = item.created_date ? (Date.now() - new Date(item.created_date).getTime()) / 86400000 : 0;
    var ageColor = ageDays > 7 ? '#ef4444' : ageDays > 3 ? '#f59e0b' : '#22c55e';
    if (item.status === 'resolved') ageColor = '#22c55e'; // always green for resolved

    // Source app color dot
    var srcApp = projectLabelOf(item);
    var srcColor = APP_COLORS[srcApp] || '#64748b';

    // Pipeline progress (0-1)
    var prStageIdx = pr ? PIPELINE_STAGES.indexOf(pr.stage) : -1;
    var prProgress = prStageIdx >= 0 ? (prStageIdx + 1) / PIPELINE_STAGES.length : 0;

    // Hover preview text
    var previewText = item.description || item.steps_to_reproduce || item.ai_summary || '';
    if (previewText.length > 120) previewText = previewText.substring(0, 120) + '...';

    return (
      <div data-testid="fb-feedback-clickable-div" key={item.id} onClick={() => setSelectedItemId(item.id)}
        className="relative rounded-xl cursor-pointer transition-all group"
        style={{
          background: isSelected ? 'rgba(99,102,241,.06)' : 'rgba(30,34,53,.7)',
          border: isSelected ? '1px solid rgba(99,102,241,.4)' : '0.5px solid rgba(71,75,100,.3)',
          padding: '0',
          overflow: 'hidden'
        }}>
        {/* Age indicator bar — left edge */}
        <div style={{position:'absolute',left:0,top:0,bottom:0,width:'3px',borderRadius:'12px 0 0 12px',background:ageColor,opacity:0.7}} />

        <div style={{padding:'10px 12px 10px 14px'}}>
          <div style={{display:'flex',alignItems:'flex-start',gap:'8px'}}>
            {/* Source app color dot */}
            <div style={{width:'7px',height:'7px',borderRadius:'50%',marginTop:'5px',flexShrink:0,background:srcApp ? srcColor : (isBug ? '#f87171' : '#34d399'),border:srcApp ? 'none' : 'none'}} title={srcApp || (isBug ? 'Bug' : 'Feature')} />
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:'13px',fontWeight:500,color:'#e8e8ed',lineHeight:'1.4',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{item.title || '(untitled)'}</div>

              {/* Pills row */}
              <div style={{display:'flex',flexWrap:'wrap',gap:'3px',marginTop:'5px'}}>
                {item.captain && <span style={{fontSize:'9px',padding:'1px 5px',borderRadius:'3px',background: item.captain_override ? 'rgba(245,158,11,.12)' : 'rgba(56,189,248,.10)',color: item.captain_override ? '#fbbf24' : '#7dd3fc',border: item.captain_override ? '0.5px solid rgba(245,158,11,.3)' : '0.5px solid rgba(56,189,248,.3)',fontWeight:500}} title={item.captain + (item.captain_override ? ' (admin override)' : ' has the plane')}>✈ {(item.captain.split('@')[0] || item.captain).slice(0, 8)}</span>}
                {item.cluster_id && <span style={{fontSize:'9px',padding:'1px 5px',borderRadius:'3px',background:'rgba(139,92,246,.08)',color:'#a78bfa',border:'0.5px solid rgba(139,92,246,.2)',fontWeight:500}}>{item.cluster_id}</span>}
                {item.complexity && <span style={{fontSize:'9px',padding:'1px 5px',borderRadius:'3px',fontWeight:500,background:item.complexity === 'complex' ? 'rgba(239,68,68,.08)' : item.complexity === 'moderate' ? 'rgba(245,158,11,.08)' : 'rgba(16,185,129,.08)',color:item.complexity === 'complex' ? '#fca5a5' : item.complexity === 'moderate' ? '#fbbf24' : '#6ee7b7'}}>{item.complexity}</span>}
                {(item.priority === 'high' || item.priority === 'critical') && <span style={{fontSize:'9px',padding:'1px 5px',borderRadius:'3px',fontWeight:500,background:item.priority === 'critical' ? 'rgba(239,68,68,.08)' : 'rgba(245,158,11,.08)',color:item.priority === 'critical' ? '#fca5a5' : '#fbbf24'}}>{item.priority}</span>}
                {item.possible_duplicate_of && <span style={{fontSize:'9px',padding:'1px 5px',borderRadius:'3px',background:'rgba(239,68,68,.08)',color:'#fca5a5',fontWeight:500,cursor:'pointer'}} onClick={(e) => { e.stopPropagation(); setSelectedItemId(item.possible_duplicate_of); }} title={'Possible duplicate of #' + item.possible_duplicate_of}>⚠ dup?</span>}
                {hasBrief && item.status !== 'resolved' && <span style={{fontSize:'9px',padding:'1px 5px',borderRadius:'3px',background:'rgba(16,185,129,.08)',color:'#6ee7b7',fontWeight:500}}>brief</span>}
              </div>

              {/* Pipeline mini-bar */}
              {pr && pr.stage !== 'resolved' && pr.stage !== 'cancelled' && (
                <div style={{marginTop:'6px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'3px'}}>
                    <span style={{fontSize:'9px',color:'#a5b4fc',fontWeight:600}}>{pr.run_id}</span>
                    <span style={{fontSize:'9px',color:'#64748b'}}>{pr.stage.replace(/_/g,' ')}</span>
                  </div>
                  <div style={{height:'3px',borderRadius:'2px',background:'rgba(71,75,100,.3)',overflow:'hidden'}}>
                    <div style={{height:'100%',borderRadius:'2px',background: pr.stage === 'failed' ? '#ef4444' : '#818cf8',width:(prProgress*100)+'%',transition:'width .3s'}} />
                  </div>
                </div>
              )}

              {step && !pr && <div style={{marginTop:'6px',fontSize:'10px',padding:'3px 6px',borderRadius:'5px',fontWeight:500,background:step.c,color:step.tc,border:'0.5px solid '+step.bc}}>{step.text}</div>}

              {/* Footer: submitter · date · id · source */}
              <div style={{marginTop:'6px',display:'flex',alignItems:'center',gap:'4px',fontSize:'10px',color:'#64748b'}}>
                <span style={{width:'13px',height:'13px',borderRadius:'50%',background:'rgba(71,75,100,.4)',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:'7px',fontWeight:600,color:'#94a3b8'}}>{getOfficerName(item.submitted_by).charAt(0).toUpperCase()}</span>
                <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'70px'}}>{getOfficerName(item.submitted_by).split('@')[0]}</span>
                <span style={{color:'#374151'}}>·</span>
                <span>{item.created_date ? new Date(item.created_date).toLocaleDateString('en-US', {month:'short',day:'numeric'}) : ''}</span>
                <span style={{color:'#374151'}}>·</span>
                <span style={{fontFamily:'monospace',color:'#475569'}}>#{String(item.id).slice(-8)}</span>
                {srcApp && <span style={{padding:'0px 4px',borderRadius:'3px',background:srcColor+'15',color:srcColor,fontWeight:500,fontSize:'9px'}}>{srcApp}</span>}
              </div>
            </div>
          </div>

          {/* Hover preview — shows description snippet */}
          {previewText && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity" style={{marginTop:'6px',fontSize:'10px',color:'#7a8499',lineHeight:'1.4',borderTop:'0.5px solid rgba(71,75,100,.2)',paddingTop:'6px'}}>
              {previewText}
            </div>
          )}
        </div>

        {batchMode && (
          <div data-testid="fb-feedback-clickable-div-1" className="absolute top-2.5 right-2.5" onClick={e => { e.stopPropagation(); toggleBatchSelect(item.id); }}>
            {batchSelectedIds.has(item.id) ? <CheckSquare className="w-4 h-4 text-violet-400" /> : <Square className="w-4 h-4 text-slate-600" />}
          </div>
        )}
        {isAdmin && !batchMode && (
          <div data-trash-btn className="absolute top-2.5 right-2.5" onClick={e => { e.stopPropagation(); if (confirm('Delete this feedback item?')) deleteMutation.mutate(item.id); }}>
            <Trash2 className="w-3 h-3 text-slate-700 hover:text-red-400 transition-colors cursor-pointer" />
          </div>
        )}
      </div>
    );
  };

  /* ── Column KPI computation ── */
  function computeColumnKPIs(columnName, items) {
    var now = Date.now();
    var dayMs = 86400000;

    if (columnName === 'Open') {
      var ages = items.map(function(i) { return i.created_date ? (now - new Date(i.created_date).getTime()) / dayMs : 0; });
      var avgAge = ages.length > 0 ? ages.reduce(function(a,b) { return a+b; }, 0) / ages.length : 0;
      var oldest = ages.length > 0 ? Math.max.apply(null, ages) : 0;
      var highPri = items.filter(function(i) { return i.priority === 'high' || i.priority === 'critical'; }).length;
      var thisWeek = items.filter(function(i) { return i.created_date && (now - new Date(i.created_date).getTime()) < 7 * dayMs; }).length;
      return { primary: { label: 'Avg age', value: avgAge.toFixed(2) + 'd' }, secondary: [
        { label: 'Oldest', value: oldest.toFixed(2) + 'd' },
        { label: 'High/Critical', value: String(highPri) },
        { label: 'New this week', value: String(thisWeek) }
      ]};
    }

    if (columnName === 'In Progress') {
      /* Use stage_timestamps.in_progress as the start anchor — falls back to created_date for
         items that entered in_progress before timestamps were introduced. Using created_date
         would overstate time-in-stage for items that spent a long time in review first. */
      var times = items.map(function(i) {
        var ts = i.stage_timestamps && typeof i.stage_timestamps === 'object' ? i.stage_timestamps : {};
        var anchor = ts['in_progress'] || i.created_date;
        return anchor ? (now - new Date(anchor).getTime()) / dayMs : 0;
      });
      var avgTime = times.length > 0 ? times.reduce(function(a,b) { return a+b; }, 0) / times.length : 0;
      var withBrief = items.filter(function(i) { return i.latest_brief_id || findExistingBrief(i.id); }).length;
      var withPR = items.filter(function(i) { return findPipelineRun(i.id); }).length;
      var stalled = items.filter(function(i) { var pr = findPipelineRun(i.id); return pr && pr.stage !== 'resolved' && pr.stage !== 'cancelled' && pr.updated_date && (now - new Date(pr.updated_date).getTime()) > 2 * 3600000; }).length;
      return { primary: { label: 'Avg time in stage', value: avgTime.toFixed(2) + 'd' }, secondary: [
        { label: 'With briefs', value: String(withBrief) },
        { label: 'Pipeline runs', value: String(withPR) },
        { label: 'Stalled (>2h)', value: String(stalled) }
      ]};
    }

    if (columnName === 'Verifying') {
      var pending = items.filter(function(i) { var r = testRuns.find(function(tr) { return String(tr.bug_id) === String(i.id); }); return r && r.status === 'pending'; }).length;
      var running = items.filter(function(i) { var r = testRuns.find(function(tr) { return String(tr.bug_id) === String(i.id); }); return r && r.status === 'running'; }).length;
      return { primary: { label: 'In queue', value: String(pending) }, secondary: [
        { label: 'Running now', value: String(running) },
        { label: 'Total', value: String(items.length) }
      ]};
    }

    if (columnName === 'Resolved') {
      /* Use stage_timestamps.resolved as the end anchor — this is set when status transitions to
         resolved and is immutable. updated_date is noisy (changes on every edit) so using it
         overstates cycle time for items that were edited after resolution. */
      var cycleTimes = items.filter(function(i) { return i.created_date; }).map(function(i) {
        var ts = i.stage_timestamps && typeof i.stage_timestamps === 'object' ? i.stage_timestamps : {};
        var endAnchor = ts['resolved'] || i.updated_date;
        return endAnchor ? (new Date(endAnchor).getTime() - new Date(i.created_date).getTime()) / dayMs : 0;
      });
      var avgCycle = cycleTimes.length > 0 ? cycleTimes.reduce(function(a,b) { return a+b; }, 0) / cycleTimes.length : 0;
      /* "Resolved this week" uses stage_timestamps.resolved for accuracy, falls back to updated_date */
      var thisWeekR = items.filter(function(i) {
        var ts = i.stage_timestamps && typeof i.stage_timestamps === 'object' ? i.stage_timestamps : {};
        var resolvedAt = ts['resolved'] || i.updated_date;
        return resolvedAt && (now - new Date(resolvedAt).getTime()) < 7 * dayMs;
      }).length;
      var avgAttempts = items.filter(function(i) { return i.fix_cycle_count; }).map(function(i) { return i.fix_cycle_count; });
      var meanAttempts = avgAttempts.length > 0 ? avgAttempts.reduce(function(a,b) { return a+b; }, 0) / avgAttempts.length : 0;
      return { primary: { label: 'Avg cycle time', value: avgCycle.toFixed(2) + 'd' }, secondary: [
        { label: 'Resolved this week', value: String(thisWeekR) },
        { label: 'Avg attempts', value: meanAttempts.toFixed(1) }
      ]};
    }

    if (columnName === 'Stuck') {
      var rejCount = items.filter(function(i) { return i.status === 'rejected'; }).length;
      var needsInfo = items.filter(function(i) { return i.status === 'needs_info'; }).length;
      var failedRuns = items.filter(function(i) { var r = testRuns.find(function(tr) { return String(tr.bug_id) === String(i.id); }); return r && r.status === 'failed'; }).length;
      return { primary: { label: 'Blocked items', value: String(items.length) }, secondary: [
        { label: 'Rejected', value: String(rejCount) },
        { label: 'Needs info', value: String(needsInfo) },
        { label: 'Failed tests', value: String(failedRuns) }
      ]};
    }

    return { primary: { label: 'Count', value: String(items.length) }, secondary: [] };
  }

  const renderColumn = (title, emoji, items, colorCls, countCls) => {
    var groups = groupByCluster(items);
    var kpis = computeColumnKPIs(title, items);
    var isExpanded = expandedKPI === title;
    return (
      <div className="flex-shrink-0 w-80 flex flex-col min-h-0" style={{borderRight:'0.5px solid rgba(71,75,100,.2)'}}>
        <div style={{padding:'12px 16px',borderBottom:'0.5px solid rgba(71,75,100,.2)',background:'rgba(15,17,23,.5)',position:'sticky',top:0,zIndex:10}}>
          <div style={{fontSize:'11px',fontWeight:600,textTransform:'uppercase',letterSpacing:'.05em',display:'flex',alignItems:'center',gap:'6px'}} className={colorCls}>
            <span style={{fontSize:'13px'}}>{emoji}</span> {title} <span style={{fontSize:'10px',padding:'1px 8px',borderRadius:'10px',fontWeight:600}} className={countCls}>{items.length}</span>
            <span style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:'4px'}}>
              <span style={{fontSize:'10px',fontWeight:600,color:'#94a3b8',fontFamily:"'JetBrains Mono',monospace"}}>{kpis.primary.value}</span>
              <span style={{fontSize:'9px',color:'#64748b'}}>{kpis.primary.label}</span>
              {kpis.secondary.length > 0 && (
                <button onClick={(e) => { e.stopPropagation(); setExpandedKPI(isExpanded ? null : title); }}
                  style={{background:'none',border:'none',cursor:'pointer',fontSize:'10px',color:'#64748b',padding:'0 2px',transition:'transform .15s',transform:isExpanded ? 'rotate(90deg)' : 'rotate(0)'}}>
                  ▸
                </button>
              )}
            </span>
          </div>
          {isExpanded && kpis.secondary.length > 0 && (
            <div style={{marginTop:'6px',display:'flex',gap:'8px',flexWrap:'wrap'}}>
              {kpis.secondary.map(function(kpi, ki) {
                return <div key={ki} style={{fontSize:'10px',color:'#64748b',display:'flex',gap:'4px',alignItems:'center'}}>
                  <span style={{fontWeight:600,color:'#94a3b8',fontFamily:"'JetBrains Mono',monospace"}}>{kpi.value}</span>
                  <span>{kpi.label}</span>
                </div>;
              })}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2.5 space-y-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-800 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-700">
          {groups.map(function(group, gi) {
            if (group.type === 'cluster') return (
              <div key={group.cluster_id} style={{borderRadius:'10px',border:'0.5px solid rgba(139,92,246,.12)',overflow:'hidden'}}>
                <div style={{padding:'6px 10px',background:'rgba(139,92,246,.04)',borderBottom:'0.5px solid rgba(139,92,246,.08)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <span style={{fontSize:'10px',fontWeight:600,color:'#a78bfa'}}>🔗 {group.cluster_id} ({group.items.length})</span>
                  {group.items.length >= 2 && <button onClick={(e) => { e.stopPropagation(); handleClusterBatchBrief(group.cluster_id); }} style={{fontSize:'9px',color:'#c4b5fd',fontWeight:600,background:'none',border:'none',cursor:'pointer'}}>📦 Batch</button>}
                </div>
                <div style={{padding:'6px',display:'flex',flexDirection:'column',gap:'6px'}}>{group.items.map(renderCard)}</div>
              </div>
            );
            return group.items.map(renderCard);
          })}
          {items.length === 0 && <div style={{textAlign:'center',fontSize:'11px',color:'#475569',padding:'32px 0'}}>No items</div>}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <style>{`[data-trash-btn]{opacity:0;transition:opacity .15s}*:hover>[data-trash-btn]{opacity:1}`}</style>
      {/* ── TOP BAR ── */}
      <div className="flex items-center justify-between px-5 py-2.5 bg-slate-900/80 border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-4">
          <h1 className="text-base font-bold text-white flex items-center gap-2"><span className="text-sky-400 font-extrabold">Feedback</span></h1>
          <div className="flex gap-0.5 bg-slate-800/60 p-0.5 rounded-lg">
            <button onClick={() => setPageMode('feedback')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${pageMode === 'feedback' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}>
              <MessageSquare className="w-3.5 h-3.5 inline mr-1" />BugBuster
            </button>
            <button onClick={() => setPageMode('briefs')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${pageMode === 'briefs' ? 'bg-violet-600/80 text-white' : 'text-slate-400 hover:text-violet-300'}`}>
              <FileText className="w-3.5 h-3.5 inline mr-1" />BriefFixer
            </button>
            <button onClick={() => setPageMode('testercaster')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${pageMode === 'testercaster' ? 'bg-cyan-600/80 text-white' : 'text-slate-400 hover:text-cyan-300'}`}>
              <FlaskConical className="w-3.5 h-3.5 inline mr-1" />TesterCaster
            </button>
          </div>
        </div>
        {pageMode === 'feedback' && (
          <div className="flex gap-2 items-center">
            <Button size="sm" onClick={async () => {
              try {
                var qResult = await base44.functions.invoke('pipelineApi', { action: 'queue:list' });
                var qData = qResult?.data || qResult;
                if (!qData.projects || qData.projects.length === 0) {
                  alert('Queue is empty — no items queued for dispatch.');
                  return;
                }
                /* Fire event for extension to open tabs per project */
                try {
                  window.dispatchEvent(new CustomEvent('compass-review-queue', {
                    detail: { projects: qData.projects }
                  }));
                } catch(e) {}
                /* Fallback: open first project manually if extension not installed */
                if (!window.__compassQueueHandled) {
                  var firstProj = qData.projects[0];
                  if (firstProj.claude_project_url) {
                    window.open(firstProj.claude_project_url, '_blank');
                  }
                  alert('Queue: ' + qData.total_queued + ' items across ' + qData.projects.length + ' project(s).\n\n' +
                    qData.projects.map(function(p) { return p.project + ': ' + p.item_count + ' items'; }).join('\n') +
                    '\n\nInstall the COMPASS extension for auto-dispatch to each project.');
                }
              } catch(e) { alert('Failed to load queue: ' + e.message); }
            }} variant="outline"
              className="h-8 text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
              <ClipboardList className="w-3.5 h-3.5 mr-1" />Review Queue
            </Button>
            <Button size="sm" onClick={() => batchMode ? exitBatchMode() : setBatchMode(true)} variant={batchMode ? 'default' : 'outline'}
              className={`h-8 text-xs ${batchMode ? 'bg-violet-600 hover:bg-violet-700 text-white' : 'border-violet-500/30 text-violet-400 hover:bg-violet-500/10'}`}>
              {batchMode ? <><X className="w-3.5 h-3.5 mr-1" /> Exit Batch</> : <><CheckSquare className="w-3.5 h-3.5 mr-1" /> Batch</>}
            </Button>
            <Button size="sm" onClick={() => setSortByDate(!sortByDate)} variant="outline"
              className={`h-8 text-xs ${sortByDate ? 'border-sky-500/30 text-sky-400 hover:bg-sky-500/10' : 'border-slate-600 text-slate-400 hover:bg-slate-800'}`}>
              <ArrowUpDown className="w-3.5 h-3.5 mr-1" />{sortByDate ? 'Newest first' : 'Unsorted'}
            </Button>
            <div className="relative">
              <Button size="sm" onClick={() => { setDeployDropdown(!deployDropdown); setBundleDropdown(false); }} disabled={markingDeployed} variant="outline"
                className={`h-8 text-xs border-emerald-500/30 hover:bg-emerald-500/10 ${isStale ? 'text-amber-400' : 'text-emerald-400'}`}>
                {markingDeployed ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Deploying</> : <><CheckCircle className="w-3.5 h-3.5 mr-1" />Deploy & Verify{isStale ? ' ('+totalStale+')' : ''} <ChevronDown className="w-3 h-3 ml-0.5" /></>}
              </Button>
              {deployDropdown && (
                <>
                  <div data-testid="fb-feedback-clickable-div-2" className="fixed inset-0 z-40" onClick={() => setDeployDropdown(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-slate-900 border border-slate-700 rounded-lg shadow-xl w-56 py-1">
                    <button onClick={() => handleDeployAndVerifyOpen('')} className="w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-slate-800 flex justify-between items-center">
                      <span>All domains</span><span className="text-[10px] text-slate-500">{testTargets.length} targets</span>
                    </button>
                    {domains.map(function(d) {
                      return (<button key={d.domain} onClick={() => handleDeployAndVerifyOpen(d.domain)} className="w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-slate-800 flex justify-between items-center">
                        <span>{d.label}</span><span className={`text-[10px] ${d.stale > 0 ? 'text-amber-400' : 'text-slate-500'}`}>{d.total}{d.stale > 0 ? ' · '+d.stale+' stale' : ''}</span>
                      </button>);
                    })}
                  </div>
                </>
              )}
              {deployVerifyDialog && (
                <>
                  <div data-testid="fb-feedback-clickable-div-3" className="fixed inset-0 z-40" onClick={() => setDeployVerifyDialog(null)} />
                  <div data-testid="fb-feedback-clickable-div-4" className="absolute right-0 top-full mt-1 z-50 bg-slate-900 border border-slate-700 rounded-xl p-4 w-96 shadow-xl shadow-black/30 space-y-3 max-h-[70vh] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-800 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-700" onClick={e => e.stopPropagation()}>
                    <div>
                      <h3 className="text-white font-semibold text-xs">Deploy & Verify</h3>
                      <p className="text-[10px] text-slate-400 mt-1">Mark {deployVerifyDialog.domain ? '"'+deployVerifyDialog.domain+'"' : 'all'} targets deployed, then auto-verify selected bugs.</p>
                    </div>
                    {deployVerifyDialog.bugs.length === 0 ? (
                      <div className="p-3 text-center text-slate-500 text-xs">No in-progress bugs. Targets will still be marked deployed.</div>
                    ) : (() => {
                      var briefReadyBugs = deployVerifyDialog.bugs.filter(function(b) { return b.latest_brief_id || fixBriefs.some(function(fb) { return Array.isArray(fb.bug_ids) && fb.bug_ids.indexOf(String(b.id)) !== -1; }); });
                      var briefReadyIds = new Set(briefReadyBugs.map(function(b) { return b.id; }));
                      return (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-slate-400 font-semibold">{deployVerifySelected.size} selected · {briefReadyBugs.length} ready</span>
                          <div className="flex gap-2">
                            <button onClick={() => setDeployVerifySelected(new Set(briefReadyBugs.map(b => b.id)))} className="text-[10px] text-indigo-400 hover:text-indigo-300">All ready</button>
                            <button onClick={() => setDeployVerifySelected(new Set())} className="text-[10px] text-slate-500 hover:text-slate-300">Clear</button>
                          </div>
                        </div>
                        {deployVerifyDialog.bugs.map(function(bug) {
                          var hasBrief = briefReadyIds.has(bug.id);
                          var sel = deployVerifySelected.has(bug.id);
                          return (<label key={bug.id} className={`flex items-start gap-2 p-2 rounded-lg border transition-colors ${!hasBrief ? 'opacity-60 cursor-not-allowed border-slate-700/30' : sel ? 'bg-indigo-500/8 border-indigo-500/25 cursor-pointer' : 'border-slate-700/50 hover:bg-slate-800/50 cursor-pointer'}`}>
                            <input data-testid="fb-feedback-input" type="checkbox" checked={sel} disabled={!hasBrief} onChange={() => { if (!hasBrief) return; setDeployVerifySelected(function(p) { var n = new Set(p); if (n.has(bug.id)) n.delete(bug.id); else n.add(bug.id); return n; }); }} className="mt-0.5 rounded border-slate-600 disabled:opacity-40" />
                            <div className="flex-1 min-w-0">
                              <span className="text-xs text-slate-200 font-medium block truncate">{bug.title}</span>
                              <span className="text-[10px] text-slate-500">{bug.type === 'bug' ? '🐛' : '💡'} {hasBrief ? <span className="text-emerald-400 ml-1">Brief ready</span> : <span className="text-amber-400 ml-1">⚠ needs brief</span>}</span>
                            </div>
                          </label>);
                        })}
                      </div>);
                    })()}
                    <div className="flex gap-2 pt-1">
                      <button onClick={handleDeployAndVerifyConfirm} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg h-8 text-xs font-semibold" data-testid="fb-deploy-btn">Deploy{deployVerifySelected.size > 0 ? ' & Verify '+deployVerifySelected.size : ''}</button>
                      <button onClick={() => setDeployVerifyDialog(null)} className="px-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg h-8 text-xs">Cancel</button>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="relative">
              <Button size="sm" onClick={() => { setBundleDropdown(!bundleDropdown); setDeployDropdown(false); }} disabled={bundleLoading} variant="outline"
                className="h-8 text-xs border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10">
                {bundleLoading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Exporting</> : <><Package className="w-3.5 h-3.5 mr-1" />Bundle <ChevronDown className="w-3 h-3 ml-0.5" /></>}
              </Button>
              {bundleDropdown && (
                <>
                  <div data-testid="fb-feedback-clickable-div-5" className="fixed inset-0 z-40" onClick={() => setBundleDropdown(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-slate-900 border border-slate-700 rounded-lg shadow-xl w-56 py-1">
                    <button onClick={() => handleExportBundle('')} className="w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-slate-800">All domains</button>
                    {domains.map(function(d) { return (<button key={d.domain} onClick={() => handleExportBundle(d.domain)} className="w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-slate-800">{d.label}</button>); })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── TESTERCASTER MODE ── */}
      {pageMode === 'testercaster' && (
        <div className="flex-1 overflow-y-auto p-6 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-800 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-700">
          <div className="flex items-start justify-between mb-4">
            <div><h1 className="text-3xl font-bold text-white mb-2">🧪 TesterCaster</h1><p className="text-slate-400">Quality Verification Dashboard</p></div>
          </div>
          <TesterCasterPanel />
        </div>
      )}

      {/* ── BRIEF VIEWER MODE ── */}
      {pageMode === 'briefs' && (
        <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-800 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-700"><BriefViewer initialBriefId={selectedBriefId} /></div>
      )}

      {/* ── PIPELINE / KANBAN MODE ── */}
      {pageMode === 'feedback' && (
        <div className="flex flex-col flex-1 min-h-0">
          {/* ── Stats bar ── */}
          <FeedbackStatsBar feedback={feedback} />
          {/* Dashboard strip */}
          <div className="flex items-center gap-3 px-5 py-2 bg-slate-900/40 border-b border-slate-800 shrink-0 overflow-x-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-800 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-700">
            {[
              { label: 'Review', val: columns.needsReview.length, c: 'text-sky-400', bg: 'bg-sky-500/10' },
              { label: 'Working', val: columns.inProgress.length, c: 'text-amber-400', bg: 'bg-amber-500/10' },
              { label: 'Verifying', val: columns.verifying.length, c: 'text-violet-400', bg: 'bg-violet-500/10' },
              { label: 'Resolved', val: columns.resolved.length, c: 'text-emerald-400', bg: 'bg-emerald-500/10' },
              { label: 'Stuck', val: columns.stuck.length, c: 'text-red-400', bg: 'bg-red-500/10' },
            ].map(function(m) {
              return (<div key={m.label} className={`flex-shrink-0 px-4 py-2 rounded-lg ${m.bg} border border-transparent cursor-default`}>
                <div className={`text-lg font-extrabold leading-none ${m.c}`}>{m.val}</div>
                <div className="text-[9px] text-slate-500 font-semibold uppercase tracking-wider mt-1">{m.label}</div>
              </div>);
            })}
            <div className="h-8 w-px bg-slate-800 mx-1 flex-shrink-0" />
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {['all','bug','feature'].map(function(t) {
                return (<button key={t} onClick={() => setActiveTab(t)}
                  className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors ${activeTab === t ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-slate-300'}`}>
                  {t === 'all' ? 'All' : t === 'bug' ? '🐛 Bugs' : '💡 Features'}
                </button>);
              })}
            </div>
            <div className="h-8 w-px bg-slate-800 mx-1 flex-shrink-0" />
            <input data-testid="fb-search-input" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search..." className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-600 w-48 focus:outline-none focus:border-indigo-500 flex-shrink-0" />
            <div className="flex-shrink-0 text-[10px] text-slate-600 ml-2">{feedback.length} total · {bugCount} bugs · {featureCount} features</div>
          </div>

          {/* Batch panel */}
          {batchMode && (
            <div className="px-5 py-3 bg-violet-500/5 border-b border-violet-500/20 shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-violet-300">{batchSelectedIds.size} selected</span>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleBatchBrief} disabled={batchSelectedIds.size === 0 || batchBriefLoading} className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-7">
                    {batchBriefLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Building</> : <><FileText className="w-3 h-3 mr-1" />Batch Brief</>}
                  </Button>
                  <Button size="sm" onClick={handleBatchVerify} disabled={batchSelectedIds.size === 0 || batchVerifyLoading} className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs h-7">
                    {batchVerifyLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Verifying</> : <><FlaskConical className="w-3 h-3 mr-1" />Batch Verify</>}
                  </Button>
                </div>
              </div>
              <Textarea data-testid="fb-strategy-note-optional-textarea" value={strategyNote} onChange={(e) => setStrategyNote(e.target.value)} placeholder="Strategy note (optional)..."
                className="bg-slate-900/60 border-slate-700 text-slate-300 text-xs min-h-[40px] resize-y placeholder:text-slate-600" />
              {batchVerifyMessage && <div className={`mt-2 px-3 py-1.5 rounded text-xs ${batchVerifyMessage.type === 'success' ? 'bg-emerald-500/10 text-emerald-400' : batchVerifyMessage.type === 'warn' ? 'bg-amber-500/10 text-amber-400' : 'bg-red-500/10 text-red-400'}`}>{batchVerifyMessage.text}</div>}
              {batchVerifyProgress && <div className="mt-2 px-3 py-1.5 rounded text-xs bg-cyan-500/10 text-cyan-400 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />{batchVerifyProgress.current}/{batchVerifyProgress.total}: {batchVerifyProgress.bugTitle} — {batchVerifyProgress.phase}</div>}
            </div>
          )}

          {/* Batch brief panel */}
          {batchBrief && (
            <div className="mx-5 mt-3 bg-slate-950/40 border border-violet-500/20 rounded-lg shrink-0">
              <div className="px-4 py-2 bg-violet-500/8 border-b border-violet-500/20 flex items-center justify-between">
                <span className="text-xs font-bold text-violet-300">Batch Brief — {batchSelectedIds.size} bugs</span>
                <div className="flex gap-2">
                  <Button data-testid="fb-dismiss-btn" size="sm" onClick={handleCopyBatchBrief} className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-7">{batchBriefCopied ? '✓ Copied' : 'Copy'}</Button>
                  <Button data-testid="fb-dismiss-btn-1" size="sm" onClick={handleOpenBatchBriefAsPage} disabled={batchBriefSaving} className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs h-7">{batchBriefSaving ? 'Saving...' : 'Save & View'}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setBatchBrief(null)} className="text-slate-500 text-xs h-7">Dismiss</Button>
                </div>
              </div>
              <div className="px-4 py-3 max-h-[200px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-800 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-700"><pre className="text-[11px] text-slate-400 whitespace-pre-wrap font-mono leading-relaxed">{batchBrief}</pre></div>
            </div>
          )}

          {/* Triage recommendations bar */}
          {(() => {
            var handleRunTriage = async () => {
              setTriageLoading(true);
              try {
                var result = await base44.functions.invoke('testerCasterSeedTests', { action: 'triage_sweep' });
                var data = result.data || result;
                setTriageData(data);
                if (data.recommendations && data.recommendations.length > 0) setTriageExpanded(true);
              } catch(e) {
                setTriageData({ error: e.message });
              } finally {
                setTriageLoading(false);
              }
            };

            var handleTriageAction = async (rec) => {
              if (rec.type === 'stale' || rec.type === 'needs_info_stale') {
                if (confirm('Mark "' + rec.title + '" as stale?')) {
                  try {
                    await base44.entities.Feedback.update(rec.feedback_id, { status: 'stale' });
                    queryClient.refetchQueries({ queryKey: ['feedback'] });
                    setTriageData(function(prev) {
                      if (!prev) return prev;
                      return { ...prev, recommendations: prev.recommendations.filter(function(r) { return r.feedback_id !== rec.feedback_id; }) };
                    });
                  } catch(e) { alert('Failed: ' + e.message); }
                }
              } else if (rec.type === 'unresolved_duplicate') {
                if (confirm('Close "' + rec.title + '" as duplicate of #' + rec.duplicate_of + '?')) {
                  try {
                    await base44.entities.Feedback.update(rec.feedback_id, { status: 'duplicate' });
                    queryClient.refetchQueries({ queryKey: ['feedback'] });
                    setTriageData(function(prev) {
                      if (!prev) return prev;
                      return { ...prev, recommendations: prev.recommendations.filter(function(r) { return r.feedback_id !== rec.feedback_id; }) };
                    });
                  } catch(e) { alert('Failed: ' + e.message); }
                }
              } else if (rec.type === 'stalled_pipeline') {
                setSelectedItemId(rec.feedback_id);
              }
            };

            var typeIcons = { stale: '🕸', stalled_pipeline: '⚠️', unresolved_duplicate: '👯', needs_info_stale: '💤' };
            var typeColors = { stale: '#64748b', stalled_pipeline: '#f59e0b', unresolved_duplicate: '#a78bfa', needs_info_stale: '#64748b' };

            return (
              <div style={{padding:'0 20px',paddingTop:triageData ? '8px' : '0',paddingBottom:triageData ? '4px' : '0',background:'transparent',flexShrink:0}}>
                {!triageData && (
                  <button onClick={handleRunTriage} disabled={triageLoading}
                    style={{fontSize:'10px',color:'#64748b',background:'none',border:'none',cursor:triageLoading ? 'wait' : 'pointer',padding:'4px 0',display:'flex',alignItems:'center',gap:'4px'}}>
                    {triageLoading ? '⏳ Scanning...' : '🔍 Run triage sweep'}
                  </button>
                )}
                {triageData && !triageData.error && (
                  <div>
                    <div style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer'}} onClick={() => setTriageExpanded(!triageExpanded)}>
                      <span style={{fontSize:'10px',fontWeight:600,color: triageData.recommendations_count > 0 ? '#fbbf24' : '#22c55e'}}>
                        {triageData.recommendations_count > 0 ? '⚠️ ' + triageData.recommendations_count + ' items need attention' : '✅ All clear'}
                      </span>
                      <span style={{fontSize:'9px',color:'#475569'}}>
                        {triageData.stats.stale_count > 0 ? triageData.stats.stale_count + ' stale · ' : ''}
                        {triageData.stats.stalled_count > 0 ? triageData.stats.stalled_count + ' stalled · ' : ''}
                        {triageData.stats.duplicate_count > 0 ? triageData.stats.duplicate_count + ' duplicates' : ''}
                      </span>
                      <span style={{fontSize:'10px',color:'#475569',transition:'transform .15s',transform:triageExpanded ? 'rotate(90deg)' : 'rotate(0)'}}>▸</span>
                      <button onClick={(e) => { e.stopPropagation(); handleRunTriage(); }} style={{fontSize:'9px',color:'#475569',background:'none',border:'none',cursor:'pointer',marginLeft:'auto'}}>↻ refresh</button>
                    </div>
                    {triageExpanded && triageData.recommendations && triageData.recommendations.length > 0 && (
                      <div style={{marginTop:'6px',display:'flex',flexDirection:'column',gap:'4px',maxHeight:'150px',overflowY:'auto'}}>
                        {triageData.recommendations.map(function(rec, ri) {
                          return (
                            <div key={ri} style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'10px',padding:'4px 8px',borderRadius:'6px',background:'rgba(30,34,53,.6)',border:'0.5px solid rgba(71,75,100,.2)'}}>
                              <span>{typeIcons[rec.type] || '📋'}</span>
                              <span style={{flex:1,color:'#cbd5e1',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{rec.suggestion}</span>
                              <button onClick={() => handleTriageAction(rec)}
                                style={{fontSize:'9px',padding:'2px 8px',borderRadius:'4px',border:'0.5px solid ' + (typeColors[rec.type] || '#64748b') + '40',background:(typeColors[rec.type] || '#64748b') + '10',color:typeColors[rec.type] || '#64748b',cursor:'pointer',fontWeight:600,flexShrink:0}}>
                                {rec.type === 'stale' || rec.type === 'needs_info_stale' ? 'Archive' : rec.type === 'unresolved_duplicate' ? 'Close dup' : 'View'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Kanban columns */}
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center text-slate-500">Loading...</div>
          ) : (
            <div className="flex flex-1 overflow-x-auto min-h-0 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-800 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-700">
              {renderColumn('Open', '👀', columns.needsReview, 'text-sky-400', 'bg-sky-500/10 text-sky-400')}
              {renderColumn('In Progress', '🔧', columns.inProgress, 'text-amber-400', 'bg-amber-500/10 text-amber-400')}
              {renderColumn('Verifying', '🧪', columns.verifying, 'text-violet-400', 'bg-violet-500/10 text-violet-400')}

              {/* Resolved column — with load more */}
              {(() => {
                var resolvedKpis = computeColumnKPIs('Resolved', columns.resolved);
                var isResExp = expandedKPI === 'Resolved';
                return (
              <div className="flex-shrink-0 w-80 flex flex-col border-r border-slate-800/50 min-h-0">
                <div className="px-4 py-3 border-b border-slate-800/50 bg-slate-900/60 sticky top-0 z-10">
                  <div className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 text-emerald-400">
                    <span>✅</span> Resolved <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-500/10 text-emerald-400">{columns.resolved.length}</span>
                    <span style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:'4px'}}>
                      <span style={{fontSize:'10px',fontWeight:600,color:'#94a3b8',fontFamily:"'JetBrains Mono',monospace"}}>{resolvedKpis.primary.value}</span>
                      <span style={{fontSize:'9px',color:'#64748b'}}>{resolvedKpis.primary.label}</span>
                      <button onClick={(e) => { e.stopPropagation(); setExpandedKPI(isResExp ? null : 'Resolved'); }}
                        style={{background:'none',border:'none',cursor:'pointer',fontSize:'10px',color:'#64748b',padding:'0 2px',transition:'transform .15s',transform:isResExp ? 'rotate(90deg)' : 'rotate(0)'}}>
                        ▸
                      </button>
                    </span>
                  </div>
                  {isResExp && resolvedKpis.secondary.length > 0 && (
                    <div style={{marginTop:'6px',display:'flex',gap:'8px',flexWrap:'wrap'}}>
                      {resolvedKpis.secondary.map(function(kpi, ki) {
                        return <div key={ki} style={{fontSize:'10px',color:'#64748b',display:'flex',gap:'4px',alignItems:'center'}}>
                          <span style={{fontWeight:600,color:'#94a3b8',fontFamily:"'JetBrains Mono',monospace"}}>{kpi.value}</span>
                          <span>{kpi.label}</span>
                        </div>;
                      })}
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-2.5 space-y-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-800 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-700">
                  {columns.resolved.slice(0, resolvedLimit).map(function(item) {
                    return (
                      <div data-testid="fb-feedback-clickable-div-6" key={item.id} onClick={() => setSelectedItemId(item.id)}
                        className={`relative rounded-lg p-2.5 cursor-pointer transition-all border opacity-70 hover:opacity-100 group ${selectedItemId === item.id ? 'border-indigo-500 bg-indigo-500/5' : 'border-slate-800 bg-slate-900/80 hover:border-slate-600'}`}>
                        <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${item.type === 'bug' ? 'bg-red-500/50' : 'bg-emerald-400/50'}`} />
                        <div className="ml-1.5">
                          <div className="text-xs font-semibold text-white/80 leading-snug truncate">{item.title}</div>
                          <div className="text-[10px] text-emerald-500 mt-1">✓ Verified {item.created_date ? format(new Date(item.created_date), 'MMM d') : ''}</div>
                        </div>
                        {isAdmin && (
                          <div data-trash-btn className="absolute top-2 right-2" onClick={e => { e.stopPropagation(); if (confirm('Delete this feedback item?')) deleteMutation.mutate(item.id); }}>
                            <Trash2 className="w-3.5 h-3.5 text-slate-600 hover:text-red-400 transition-colors cursor-pointer" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {columns.resolved.length > resolvedLimit && (
                    <button onClick={() => setResolvedLimit(function(l) { return l + 20; })}
                      className="w-full py-2.5 rounded-lg border border-dashed border-slate-700 text-[11px] text-slate-500 font-semibold hover:border-emerald-500/30 hover:text-emerald-400 transition-colors">
                      Load 20 more ({columns.resolved.length - resolvedLimit} remaining)
                    </button>
                  )}
                </div>
              </div>
                );
              })()}

              {renderColumn('Stuck', '🚨', columns.stuck, 'text-red-400', 'bg-red-500/10 text-red-400')}
            </div>
          )}
        </div>
      )}

      {/* ── DETAIL PANEL ── */}
      {selectedItem && pageMode === 'feedback' && (
        <>
          <div data-testid="fb-feedback-clickable-div-7" className="fixed inset-0 bg-black/30 z-40 backdrop-blur-[2px]" onClick={() => setSelectedItemId(null)} />
          <div className="fixed right-0 top-0 bottom-0 w-[520px] z-50 overflow-y-auto shadow-2xl [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-800 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-700" style={{background:'#161923',borderLeft:'0.5px solid rgba(71,75,100,.3)',animation:'slideDetailIn .2s ease'}}>
            <style>{`@keyframes slideDetailIn{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
            <div className="sticky top-0 z-10 px-5 py-3 flex items-center justify-between" style={{background:'rgba(22,25,35,.95)',borderBottom:'0.5px solid rgba(71,75,100,.25)',backdropFilter:'blur(8px)'}}>
              <div className="flex items-center gap-2">
                <span style={{fontSize:'10px',padding:'2px 8px',borderRadius:'4px',fontWeight:600,background:selectedItem.type === 'bug' ? 'rgba(239,68,68,.08)' : 'rgba(16,185,129,.08)',color:selectedItem.type === 'bug' ? '#fca5a5' : '#6ee7b7'}}>{selectedItem.type === 'bug' ? 'Bug' : 'Feature'}</span>
                <span style={{fontSize:'10px',fontFamily:'monospace',color:'#475569'}}>#{String(selectedItem.id).slice(-8)}</span>
              </div>
              <button onClick={() => setSelectedItemId(null)} style={{color:'#64748b',background:'none',border:'none',cursor:'pointer',fontSize:'16px',padding:'4px 8px',borderRadius:'6px'}}>✕</button>
            </div>
            <div className="p-1">
              <FeedbackCard
                item={selectedItem}
                getOfficerName={getOfficerName}
                onUpdateStatus={handleUpdateStatus}
                onUpdatePriority={handleUpdatePriority}
                onUpdateAll={handleUpdateAll}
                onDelete={(id) => deleteMutation.mutate(id)}
                onRequestInfo={handleRequestInfo}
                onReject={handleRejectFeedback}
                onPromote={handlePromoteToIssue}
                editingNotes={editingNotes}
                setEditingNotes={setEditingNotes}
                onSaveNotes={handleSaveNotes}
                batchMode={false}
                batchSelected={false}
                onBatchToggle={() => {}}
                onOpenBrief={handleOpenBrief}
                existingBrief={findExistingBrief(selectedItem.id)}
                onEnriched={() => queryClient.refetchQueries({ queryKey: ['feedback'] })}
                allFeedback={feedback}
                latestRun={testRuns.find(function(r) { return String(r.bug_id) === String(selectedItem.id); }) || null}
                onRetry={() => queryClient.refetchQueries({ queryKey: ['testRuns'] })}
                onClusterBatchBrief={handleClusterBatchBrief}
                pipelineRun={findPipelineRun(selectedItem.id)}
                onResumePipeline={(resumeData) => {
                  setLaunchpad({
                    run_id: resumeData.run_id,
                    feedback_id: resumeData.feedback_id,
                    title: resumeData.title,
                    domain: resumeData.domain,
                    brief_id: resumeData.brief_id || '',
                    project_url: resumeData.project_url || '',
                    command: resumeData.command
                  });
                }}
              />
            </div>

            {/* ── Submitter Verification Panel — shown for resolved items ── */}
            {selectedItem.status === 'resolved' && (
              <div style={{margin:'0 16px 16px',padding:'16px',borderRadius:'12px',border:'1px solid rgba(34,197,94,.2)',background:'rgba(34,197,94,.04)'}}>
                <div style={{fontSize:'13px',fontWeight:600,color:'#6ee7b7',marginBottom:'10px'}}>✅ This item has been resolved</div>
                <div style={{fontSize:'12px',color:'#94a3b8',marginBottom:'12px'}}>
                  {selectedItem.submitted_by ? 'Submitted by ' + getOfficerName(selectedItem.submitted_by) + '.' : ''}
                  {' '}Please verify the fix works and share your experience.
                </div>

                <div style={{display:'flex',gap:'8px',marginBottom:'12px'}}>
                  <button onClick={async () => {
                    try {
                      await base44.functions.invoke('pipelineApi', {
                        action: 'verification:create',
                        feedback_id: String(selectedItem.id),
                        status: 'confirmed'
                      });
                      await base44.entities.Feedback.update(selectedItem.id, { submitter_verified: 'confirmed' });
                      queryClient.refetchQueries({ queryKey: ['feedback'] });
                      alert('Thank you! Fix confirmed.');
                    } catch(e) { alert('Error: ' + e.message); }
                  }}
                    style={{flex:1,padding:'10px',fontSize:'13px',fontWeight:600,borderRadius:'8px',background:'rgba(34,197,94,.1)',color:'#6ee7b7',border:'1px solid rgba(34,197,94,.3)',cursor:'pointer'}}>
                    👍 Yes, confirmed fixed
                  </button>
                  <button onClick={async () => {
                    var notes = prompt('What is still broken? (Provide details so we can fix it)');
                    if (!notes) return;
                    try {
                      await base44.functions.invoke('pipelineApi', {
                        action: 'verification:update',
                        verification_id: 'by_feedback',
                        feedback_id: String(selectedItem.id),
                        status: 'rejected',
                        submitter_notes: notes
                      });
                      queryClient.refetchQueries({ queryKey: ['feedback'] });
                      alert('Feedback reopened — a new fix cycle will start.');
                    } catch(e) { alert('Error: ' + e.message); }
                  }}
                    style={{flex:1,padding:'10px',fontSize:'13px',fontWeight:600,borderRadius:'8px',background:'rgba(239,68,68,.06)',color:'#fca5a5',border:'1px solid rgba(239,68,68,.2)',cursor:'pointer'}}>
                    👎 Still broken
                  </button>
                </div>

                {/* Star rating */}
                <div style={{marginBottom:'8px'}}>
                  <div style={{fontSize:'11px',color:'#64748b',marginBottom:'6px'}}>How was the experience? (optional)</div>
                  <div style={{display:'flex',gap:'4px'}}>
                    {[1,2,3,4,5].map(function(star) {
                      return (
                        <button key={star} onClick={async () => {
                          try {
                            await base44.entities.Feedback.update(selectedItem.id, { submitter_rating: star });
                            queryClient.refetchQueries({ queryKey: ['feedback'] });
                          } catch(e) {}
                        }}
                          style={{fontSize:'20px',background:'none',border:'none',cursor:'pointer',
                            opacity: selectedItem.submitter_rating >= star ? 1 : 0.25,
                            filter: selectedItem.submitter_rating >= star ? 'none' : 'grayscale(1)'
                          }}>⭐</button>
                      );
                    })}
                    {selectedItem.submitter_rating > 0 && <span style={{fontSize:'11px',color:'#64748b',alignSelf:'center',marginLeft:'4px'}}>{selectedItem.submitter_rating}/5</span>}
                  </div>
                </div>
              </div>
            )}

          </div>
        </>
      )}

      {/* ── PRE-FLIGHT CHECK MODAL ── */}
      {preflightData && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[60] backdrop-blur-[2px]" onClick={() => setPreflightData(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[61] w-[480px] max-h-[80vh] overflow-y-auto" style={{animation:'launchpadIn .2s ease'}}>
            <div style={{background:'#1a1d28',border:'0.5px solid rgba(71,75,100,.4)',borderRadius:'16px',padding:'24px',boxShadow:'0 20px 60px rgba(0,0,0,.5)'}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:'12px',marginBottom:'18px'}}>
                <div style={{width:'36px',height:'36px',borderRadius:'10px',background:'rgba(99,102,241,.1)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:'18px'}}>🛫</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:'15px',fontWeight:600,color:'#e8e8ed'}}>Pre-flight check</div>
                  <div style={{fontSize:'12px',color:'#94a3b8',marginTop:'3px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{preflightData.item.title}</div>
                  <div style={{fontSize:'10px',color:'#475569',marginTop:'2px',fontFamily:'monospace'}}>#{String(preflightData.item.id)}</div>
                </div>
                <button onClick={() => setPreflightData(null)} style={{color:'#64748b',background:'none',border:'none',cursor:'pointer',fontSize:'16px',padding:'2px 6px',flexShrink:0}}>✕</button>
              </div>

              <div style={{display:'flex',flexDirection:'column',gap:'6px',marginBottom:'16px'}}>
                {preflightData.checks.map(function(c, ci) {
                  var icon = c.level === 'block' ? '🚫' : c.level === 'warn' ? '⚠️' : c.level === 'pass' ? '✅' : 'ℹ️';
                  var borderColor = c.level === 'block' ? 'rgba(239,68,68,.3)' : c.level === 'warn' ? 'rgba(245,158,11,.2)' : c.level === 'pass' ? 'rgba(34,197,94,.2)' : 'rgba(99,102,241,.15)';
                  var bgColor = c.level === 'block' ? 'rgba(239,68,68,.04)' : c.level === 'warn' ? 'rgba(245,158,11,.04)' : c.level === 'pass' ? 'rgba(34,197,94,.04)' : 'rgba(99,102,241,.04)';
                  var labelColor = c.level === 'block' ? '#fca5a5' : c.level === 'warn' ? '#fbbf24' : c.level === 'pass' ? '#6ee7b7' : '#a5b4fc';
                  return (
                    <div key={ci} style={{padding:'10px 12px',borderRadius:'10px',border:'0.5px solid '+borderColor,background:bgColor}}>
                      <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'3px'}}>
                        <span style={{fontSize:'13px'}}>{icon}</span>
                        <span style={{fontSize:'12px',fontWeight:600,color:labelColor}}>{c.label}</span>
                        {c.action && (
                          <button onClick={async (ev) => {
                            ev.stopPropagation();
                            if (c.id === 'enrich' || c.id === 'enrich_stale') {
                              // Run enrichment, then refresh pre-flight
                              try {
                                var enrichBtn = ev.currentTarget;
                                enrichBtn.textContent = 'Enriching...';
                                enrichBtn.disabled = true;
                                await base44.functions.invoke('testerCasterSeedTests', { action: 'enrich_feedback', bug_id: preflightData.item.id });
                                queryClient.refetchQueries({ queryKey: ['feedback'] });
                                enrichBtn.textContent = 'Done!';
                                setTimeout(function() { setPreflightData(null); }, 500);
                              } catch(err) { alert('Enrichment failed: ' + err.message); }
                            }
                            else if (c.id === 'dup_open' || c.id === 'dup_resolved') { setPreflightData(null); setSelectedItemId(preflightData.item.possible_duplicate_of); }
                            else if (c.id === 'stale') { base44.entities.Feedback.update(preflightData.item.id, { status: 'stale' }); setPreflightData(null); queryClient.refetchQueries({ queryKey: ['feedback'] }); }
                            else if (c.id === 'cluster_resolved') { setPreflightData(null); setSearchQuery(preflightData.item.cluster_id); }
                          }}
                            style={{marginLeft:'auto',fontSize:'10px',padding:'2px 8px',borderRadius:'4px',border:'0.5px solid '+borderColor,background:'transparent',color:labelColor,cursor:'pointer',fontWeight:600}}>
                            {c.action}
                          </button>
                        )}
                      </div>
                      <div style={{fontSize:'11px',color:'#94a3b8',lineHeight:'1.4',paddingLeft:'21px'}}>{c.msg}</div>
                    </div>
                  );
                })}
              </div>

              {/* Priority override selector */}
              <div style={{padding:'12px',borderRadius:'10px',border:'0.5px solid rgba(99,102,241,.15)',background:'rgba(99,102,241,.04)',marginBottom:'16px'}}>
                <div style={{fontSize:'11px',fontWeight:600,color:'#a5b4fc',marginBottom:'8px'}}>Priority override</div>
                <div style={{display:'flex',gap:'6px'}}>
                  {['critical','high','medium','low'].map(function(level) {
                    var colors = { critical: '#ef4444', high: '#f59e0b', medium: '#6366f1', low: '#64748b' };
                    var isSelected = (preflightData.priorityOverride || '') === level;
                    return (
                      <button key={level} onClick={() => setPreflightData(function(prev) { return { ...prev, priorityOverride: isSelected ? '' : level }; })}
                        style={{flex:1,padding:'6px',fontSize:'11px',fontWeight:600,borderRadius:'6px',textTransform:'capitalize',
                          border:'1px solid ' + (isSelected ? colors[level] : 'rgba(71,75,100,.3)'),
                          background: isSelected ? colors[level] + '18' : 'transparent',
                          color: isSelected ? colors[level] : '#64748b',
                          cursor:'pointer',transition:'all .15s'
                        }}>
                        {level}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Action buttons */}
              <div style={{display:'flex',gap:'8px'}}>
                <button onClick={async () => {
                  var pf = preflightData;
                  setPreflightData(null);
                  // Save priority override if set
                  if (pf.priorityOverride && pf.priorityOverride !== (pf.item.priority_override || '')) {
                    try { await base44.entities.Feedback.update(pf.item.id, { priority_override: pf.priorityOverride }); } catch(e) {}
                  }
                  // Continue with pipeline creation
                  await startPipeline(pf.item, pf.updatedLog);
                }}
                  style={{flex:1,padding:'10px',fontSize:'14px',fontWeight:600,borderRadius:'8px',background:preflightData.hasBlocker ? '#92400e' : '#6366f1',color:'white',border:'none',cursor:'pointer',transition:'background .15s'}}>
                  {preflightData.hasBlocker ? '⚠️ Override and proceed' : '🚀 Proceed to pipeline'}
                </button>
                <button onClick={() => setPreflightData(null)}
                  style={{padding:'10px 20px',fontSize:'13px',borderRadius:'8px',background:'transparent',border:'0.5px solid rgba(71,75,100,.4)',color:'#64748b',cursor:'pointer'}}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── PIPELINE LAUNCHPAD ── */}
      {launchpad && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60] backdrop-blur-[2px]" onClick={() => setLaunchpad(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[61] w-[420px]" style={{animation:'launchpadIn .2s ease'}}>
            <style>{`@keyframes launchpadIn{from{transform:translate(-50%,-50%) scale(.95);opacity:0}to{transform:translate(-50%,-50%) scale(1);opacity:1}}`}</style>
            <div style={{background:'#1a1d28',border:'0.5px solid rgba(71,75,100,.4)',borderRadius:'16px',padding:'20px',boxShadow:'0 20px 60px rgba(0,0,0,.5)'}}>
              <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'14px'}}>
                <div style={{width:'8px',height:'8px',borderRadius:'50%',background:'#22c55e',animation:'launchPulse 2s infinite'}} />
                <span style={{fontSize:'14px',fontWeight:500,color:'#e8e8ed'}}>Pipeline {launchpad.run_id} ready</span>
                <button onClick={() => setLaunchpad(null)} style={{marginLeft:'auto',color:'#64748b',background:'none',border:'none',cursor:'pointer',fontSize:'16px',padding:'2px 6px',borderRadius:'6px'}}>✕</button>
              </div>
              <style>{`@keyframes launchPulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>

              <div style={{background:'rgba(30,34,53,.8)',borderRadius:'10px',padding:'12px',marginBottom:'14px',border:'0.5px solid rgba(71,75,100,.2)'}}>
                <div style={{fontSize:'11px',color:'#94a3b8',marginBottom:'3px'}}>{launchpad.type === 'bug' ? 'Bug' : 'Feature'}</div>
                <div style={{fontSize:'14px',fontWeight:500,color:'#e8e8ed',marginBottom:'8px'}}>{launchpad.title}</div>
                <div style={{display:'flex',gap:'6px',flexWrap:'wrap',alignItems:'center'}}>
                  <span style={{fontSize:'10px',padding:'2px 8px',borderRadius:'4px',background:'rgba(16,185,129,.08)',color:'#6ee7b7',fontWeight:500}}>Context ready</span>
                  <span style={{fontSize:'10px',padding:'2px 8px',borderRadius:'4px',background:'rgba(99,102,241,.08)',color:'#a5b4fc',fontWeight:500}}>{launchpad.domain}</span>
                </div>
                {/* Model selector */}
                <div style={{marginTop:'10px',display:'flex',gap:'6px',alignItems:'center'}}>
                  <span style={{fontSize:'10px',color:'#64748b',fontWeight:500}}>Model:</span>
                  <button onClick={() => setLaunchpadModel('sonnet')}
                    style={{fontSize:'11px',padding:'4px 12px',borderRadius:'6px',fontWeight:600,cursor:'pointer',transition:'all .15s',
                      background: launchpadModel === 'sonnet' ? 'rgba(56,189,248,.12)' : 'transparent',
                      color: launchpadModel === 'sonnet' ? '#7dd3fc' : '#475569',
                      border: '1px solid ' + (launchpadModel === 'sonnet' ? 'rgba(56,189,248,.3)' : 'rgba(71,75,100,.3)')
                    }}>⚡ Sonnet</button>
                  <button onClick={() => setLaunchpadModel('opus')}
                    style={{fontSize:'11px',padding:'4px 12px',borderRadius:'6px',fontWeight:600,cursor:'pointer',transition:'all .15s',
                      background: launchpadModel === 'opus' ? 'rgba(239,68,68,.12)' : 'transparent',
                      color: launchpadModel === 'opus' ? '#fca5a5' : '#475569',
                      border: '1px solid ' + (launchpadModel === 'opus' ? 'rgba(239,68,68,.3)' : 'rgba(71,75,100,.3)')
                    }}>🧠 Opus</button>
                  {launchpad.model === 'opus' && launchpadModel !== 'opus' && (
                    <span style={{fontSize:'9px',color:'#f59e0b',fontStyle:'italic'}}>Opus was recommended</span>
                  )}
                  {launchpad.modelReasons && launchpad.modelReasons.length > 0 && (
                    <span style={{fontSize:'9px',color:'#475569'}} title={launchpad.modelReasons.join(', ')}>({launchpad.modelReasons.join(', ')})</span>
                  )}
                </div>
              </div>

              <div style={{fontSize:'12px',color:'#94a3b8',marginBottom:'6px'}}>Command for claude.ai:</div>
              <div style={{background:'rgba(15,17,23,.9)',borderRadius:'10px',padding:'14px',border:'1px solid rgba(99,102,241,.25)',marginBottom:'14px'}}>
                <div style={{fontFamily:'monospace',fontSize:'14px',color:'#a5b4fc',wordBreak:'break-all',lineHeight:'1.5'}}>{launchpad.command}</div>
              </div>

              <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                <div style={{display:'flex',gap:'8px'}}>
                  {/* 🚀 Dispatch — red, password-gated, auto-executes */}
                  <button onClick={() => { setDispatchPasswordOpen(true); setDispatchPassword(''); setDispatchError(''); }}
                    style={{flex:1,padding:'10px',fontSize:'14px',fontWeight:600,borderRadius:'8px',background:'#dc2626',color:'white',border:'none',cursor:'pointer',transition:'background .15s',letterSpacing:'.3px'}}>
                    🚀 Dispatch
                  </button>
                  {/* 📋 Queue — violet, adds to queue for later review */}
                  <button onClick={async () => {
                    try {
                      var qResult = await base44.functions.invoke('pipelineApi', { action: 'pipeline:queue', run_id: launchpad.run_id });
                      var qBtn = document.getElementById('launchpad-queue-btn');
                      if (qBtn) { qBtn.textContent = '✓ Queued'; qBtn.style.background = '#059669'; setTimeout(() => { qBtn.textContent = '📋 Queue'; qBtn.style.background = '#7c3aed'; }, 2000); }
                      console.log('[QUEUE] ' + launchpad.run_id + ' queued');
                    } catch(e) { alert('Queue failed: ' + e.message); }
                  }}
                    id="launchpad-queue-btn"
                    style={{flex:1,padding:'10px',fontSize:'14px',fontWeight:600,borderRadius:'8px',background:'#7c3aed',color:'white',border:'none',cursor:'pointer',transition:'background .15s',letterSpacing:'.3px'}}>
                    📋 Queue
                  </button>
                </div>
                <button onClick={() => setLaunchpad(null)}
                  style={{padding:'8px 16px',fontSize:'12px',borderRadius:'8px',background:'transparent',border:'0.5px solid rgba(71,75,100,.4)',color:'#64748b',cursor:'pointer',width:'100%'}}>
                  Dismiss
                </button>
              </div>

              {/* Dispatch password modal */}
              {dispatchPasswordOpen && (
                <div style={{marginTop:'12px',padding:'14px',borderRadius:'10px',border:'1px solid rgba(220,38,38,.3)',background:'rgba(220,38,38,.04)'}}>
                  <div style={{fontSize:'12px',fontWeight:600,color:'#fca5a5',marginBottom:'8px'}}>Enter dispatch code to execute immediately</div>
                  <div style={{display:'flex',gap:'8px'}}>
                    <input type="password" value={dispatchPassword} onChange={(e) => setDispatchPassword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') document.getElementById('dispatch-verify-btn')?.click(); }}
                      placeholder="Dispatch code..."
                      style={{flex:1,padding:'8px 12px',fontSize:'13px',borderRadius:'6px',background:'rgba(30,34,53,.8)',border:'0.5px solid rgba(71,75,100,.4)',color:'#e8e8ed',outline:'none'}} autoFocus />
                    <button id="dispatch-verify-btn" disabled={dispatchVerifying} onClick={async () => {
                      setDispatchVerifying(true);
                      setDispatchError('');
                      try {
                        var vResult = await base44.functions.invoke('pipelineApi', { action: 'dispatch:verify', code: dispatchPassword });
                        var vData = vResult?.data || vResult;
                        if (vData.verified) {
                          /* Password correct — dispatch with auto-execute */
                          navigator.clipboard.writeText(launchpad.command);
                          /* Let extension handle tab opening — NO fallback window.open */
                          try {
                            window.__compassDispatchHandled = false;
                            window.dispatchEvent(new CustomEvent('compass-dispatch', {
                              detail: { command: launchpad.command, projectUrl: launchpad.project_url || '', autoSend: true, model: launchpadModel }
                            }));
                          } catch(ex) {}
                          /* Only open manually if extension is NOT installed at all */
                          if (!window.__compassDispatchHandled) {
                            window.open(launchpad.project_url || 'https://claude.ai', '_blank');
                          }
                          setDispatchPasswordOpen(false);
                          setLaunchpad(null);
                        } else {
                          setDispatchError('Invalid code');
                        }
                      } catch(e) {
                        setDispatchError(e.message || 'Verification failed');
                      } finally {
                        setDispatchVerifying(false);
                      }
                    }}
                      style={{padding:'8px 16px',fontSize:'12px',fontWeight:600,borderRadius:'6px',background:'#dc2626',color:'white',border:'none',cursor:dispatchVerifying ? 'wait' : 'pointer'}}>
                      {dispatchVerifying ? '...' : 'Verify'}
                    </button>
                    <button onClick={() => setDispatchPasswordOpen(false)}
                      style={{padding:'8px 12px',fontSize:'12px',borderRadius:'6px',background:'transparent',border:'0.5px solid rgba(71,75,100,.4)',color:'#64748b',cursor:'pointer'}}>
                      ✕
                    </button>
                  </div>
                  {dispatchError && <div style={{marginTop:'6px',fontSize:'11px',color:'#fca5a5'}}>{dispatchError}</div>}
                </div>
              )}

              <div style={{marginTop:'10px',fontSize:'11px',color:'#475569',lineHeight:'1.5'}}>
                <b style={{color:'#fca5a5'}}>Dispatch</b> = open claude.ai and auto-execute (requires code). <b style={{color:'#a78bfa'}}>Queue</b> = add to queue for batch review later.
              </div>
            </div>
          </div>
        </>
      )}

      {/* Bot button */}
      {ReactDOM.createPortal(
        <>
          <FeedbackBotChat open={botChatOpen} onOpenChange={setBotChatOpen} />
          <button onClick={() => setBotChatOpen(!botChatOpen)}
            className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-900/40 flex items-center justify-center transition-all hover:scale-105">
            <Bot className="w-6 h-6" />
          </button>
        </>,
        document.body
      )}
    </div>
  );
}